// npm
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm, FieldValues } from 'react-hook-form';
import { debounce, isEqual } from 'lodash';
import moment from 'moment';
import { NOT_IMPLEMENTED, PARTIALLY_IMPLEMENTED, REMEDIATION_PLAN_OVERDUE, everyQuestionControlHasAnswer, generateDefaultSubRemediationsObject } from '../../../services/surveyHelpers';
import { setShowRemediationSaveAlert, setShowAssessorFindingsAlert, setNewQuestionInclusionAlert, setShowRemediationValidationAlert } from '../../../app/store/alertSlice';
import { fetchAssessment as storeFetchAssessment, updateAssessmentAssessmentSurvey, updateAssessmentSurveyArrayAssessorAnswer } from '../../../app/store/assessmentSlice';
import { setAnsweredByCertModalData } from '../../../app/store/uiSlice';
import { AssessmentQuestionStatusById, QuestionStatus, setAllAssessmentQuestionStatus, setAssessmentQuestionStatusId, setNextRemediationQuestion } from '../../../app/store/questionnaireAnswerSlice';
import { Box, Button, Card, Collapse, darken, Divider, Grid, lighten, Typography, useTheme, Snackbar, Alert, CircularProgress } from '@mui/material';
import { Check, NavigateNext, Warning, ThumbUp } from '@mui/icons-material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { createAssessmentNotification, createAssessorAnswer, deleteAssessmentNotification, handleSaveAssessorQuestionAnswer, handleSaveSubRemediations, updateAssessmentSurvey } from '../../../services/assessments';
import Slide from '@mui/material/Slide';
// hooks, slice, and interface
import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { updateAssessorAnswer } from '../../../services/assessments';
import {
  setQuestion,
  nextQuestion,
  setQuestionAnswer,
  PaletteKey,
  setReadOnly,
  setCompanyCertifications,
  setAssessmentQuestionStatus,
  setAuditLogs,
  setQuestionAuditLogFetched,
  setEvidence,
  setComments as setStoreComments,
} from '../../../app/store/questionnaireAnswerSlice';
import { AuditLog } from '../../../interfaces/auditLog.interface';
import { Question } from '../../../interfaces/question.interface';
import { Survey } from '../../../interfaces/survey.interface';
import { Questionnaire } from '../../../interfaces/questionnaire.interface';
import User, { Badge, Company, Version } from '../../../interfaces/users.interface';
import { Evidence, File, QuestionAnswer } from '../../../interfaces/questionAnswer.interface';
import { AssessmentSurvey, AssessorAnswer, Comment, PIDetails, SubRemediation, WillNotRemediateReasons } from '../../../interfaces/assessment.interface';
import { CertificationControl } from '../../../interfaces/certificationControl.interface';
import { InclusionConfiguration } from '../../../interfaces/inclusionConfiguration.interface';
import { ADMIN_REMEDIATION_VALIDATION, NOTIFICATION_TYPES, REMEDIATION_OPTIONS, REMEDIATION_VALIDATION_STATUS_UPDATE, REMEDIATION_VERIFICATION_STATUS } from '../../../services/surveyHelpers';
import { QuestionnaireTypes, isWorkflowFiveThreeOneOrGreater, isWorkflowFiveThreeOrGreater } from '../../../services/questionnaires';
import AssessorPIDetails from '../../../features/Questions/AssessorPIDetails';
// functions
import { getMultipleComments } from '../../../services/assessments';
import { buildBestPracticeTitle } from '../../BestPractices/BestPractice';
import { sanitizeRemediationQuestionAnswers, shouldUseAssessmentColors } from '../../../services/questionnaireQuestionStatus';
import { createAuditLog, createComment, getAuditLogByQuestionAnswerId, updateAuditLog } from '../../../services/commentsLogs';
import createQuestionAnswer, {
  deleteEvidence,
  getManyEvidence,
  updateEvidences,
  updateQuestionAnswer,
  uploadMultipleAttachments,
  processFiles,
  buildAnswerPayload,
} from '../../../services/questionAnswer';
//components
import Comments from '../../Comments/Comments';
import RemediationForm from './RemediationForm';
import AuditLogModal from '../../AuditLog/AuditLogModal';
import AssessorFindingsForm from './AssessorFindingsForm';
import FileUploaderComp, { FileUpload } from '../../Forms/FileUpload';
import AttachmentFormAndComment from './AttachmentAssessmentAndComment';
import QuestionOnQuestionnaireAnswer from './QuestionOnQuestionnaireAnswer';
import QuestionOnQuestionnaireHeader from './QuestionOnQuestionnaireHeader';
import StandardDialog, { StandardDialogActions } from '../../Modals/StandardDialog';
import CompanyCertificationForm from '../../Directory/Certifications/CompanyCertificationForm';
import { getQuestion, fetchIncludedInLogic } from '../../../services/question';
import { PRE_ASSESSMENT_STATUSES, ReadOnlyBaselineWorkflowStatusArray, TPN_BEST_PRACTICE_5_1, TPN_BEST_PRACTICE_5_2, TPN_BEST_PRACTICE_5_3, WORKFLOW_STATUSES, WorkflowStatus } from '../../../services/workflow';
import { USER_TYPES } from '../../../services/user';
import { LABELS } from '../../../utils/labels';
import { newlyRevealedQuestionCodes } from '../../../services/inclusionConfiguration';
import { fetchBaselineSurvey, updateWorkflow } from '../../../app/store/workflowSlice';
import { GOLD_NEXT_GEN_BADGE_IMAGE, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_IMAGE, GOLD_STAR_BADGE_LABEL, determineVerificationStatus, eligibleForGoldStarBadge, eligibleForNextGenGoldBadge, processVerificationAward } from '../../../services/badge';
import { Workflow, WorkflowTypeKey } from '../../../interfaces/workflow.interface';
import { QuestionsNeedingCertAnswersMap, useNeedsCertQARecords } from '../../../hooks/useNeedsCertQARecords';
import AutoAnsweredByCertModal from '../../Modals/AutoAnsweredByCertModal';
import { createCertificateBasedQuestionAnswers } from '../../../services/certifications';
import { BestPractice } from '../../../interfaces/bestPractice.interface';
import { AsyncSaveButton } from '../../../components/buttons/AsyncSaveButton';
import { useIsQuestionnaireArchived } from '../../../hooks/useIsQuestionnaireArchived';
import { buildAssessorAnswer, getRemediationCounts } from '../../../utils/remediationUtils';

const ColorMap: { [key: string]: PaletteKey } = {
  incomplete: 'info',
  complete: 'success',
  remediation: 'error',
  submitted: 'warning',
  cert: 'complete',
  priority: 'secondary',
  'non-compliant': 'nonCompliant',
  remediationComplete: 'primary',
};

const certAnswerGenerationInFlight = new Set<number>();
// Question IDs whose cert-based answers have already been generated, scoped per
// workflow so IDs from one workflow can't suppress generation in another.
const certAnswersGeneratedByWorkflow = new Map<number, Set<string>>();

// Cleared on Survey teardown so re-entering the same workflow regenerates cert answers
export const resetCertAnswerGenerationCache = (): void => {
  certAnswerGenerationInFlight.clear();
  certAnswersGeneratedByWorkflow.clear();
};

const QuestionOnQuestionnaire = (props: { incompleteQuestionIds?: { [key: number]: number }; question: Question; comments?: boolean, relavantQuestionStatus?: QuestionStatus }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const params = useParams();
  const surveyId:number = parseInt(params.surveyId!);
  const assessmentId:number = parseInt(params.assessmentId!);
  const { user } = useAppSelector((state) => state.user);
  const { assessment, bpVerificationList } = useAppSelector((state) => state.assessments);
  const { survey, surveys } = useAppSelector((state) => state.surveys);
  const {
    certControlsById,
    question: questionItem,
    questionStatus,
    companyCerts,
    questions,
    bestPractice,
    bpStatus,
    topic,
    domain,
    preview,
    questionnaire,
    readOnly,
    submitted,
    assessmentStatus,
    assessmentQuestionsStatus,
    comments: allComments,
    auditLogs,
    evidenceById,
    company,
  } = useAppSelector((state) => state.questionnaireAnswer);
  const { answeredByCertModalData } = useAppSelector((state) => state.uiSlice);
  const { needsCertQARecords, hasNeedsCertQARecords } = useNeedsCertQARecords(
    questionStatus,
    certControlsById,
    questionnaire,
  );
  const { showRemediationSaveAlert, showAssessorFindingsAlert, showRemediationValidationAlert } = useAppSelector((state) => state.alertSlice);
  const { applicationVersions } = useAppSelector((state) => state.serviceCategories);
  const [question, setCurrentQuestion] = useState(props.question);
  const { workflow } = useAppSelector(state => state.workflow);
  const blueBadge = useMemo(() => (workflow?.badges as Badge[])?.find(b => b.title === 'tpn_self_reported'), [workflow]);
  const [curQuestionStatus, setCurStatus] = useState<string>(props.relavantQuestionStatus?.status ? props.relavantQuestionStatus.status : 'incomplete');
  const [showPIDetails, setShowPIDetails] = useState(false);
  const [isValidCORemPlan, setIsValidCORemPlan] = useState<boolean>(false);
  const [selectedCtrlAns, setSelectedCtrlAns] = useState<{ [key: number]: PIDetails }>({});
  const isFiveThreeOrGreater = useMemo(() => workflow ? isWorkflowFiveThreeOrGreater(workflow.workflowType) : false, [workflow]);
  const [verificationAwardModalType, setVerificationAwardModalType] = useState<typeof GOLD_STAR_BADGE_LABEL | typeof GOLD_NEXT_GEN_BADGE_LABEL | undefined>(undefined);
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      text: undefined,
      answer: undefined,
      nonApplicableAnswers: undefined,
      compensatingControlAnswers: undefined,
      nonImplementedAnswers: undefined,
      comment: undefined,
    } as { text?: string; nonImplementedAnswers?: number[] | number, compensatingControlAnswers?: number[] | number, nonApplicableAnswers?: number[] | number, answer?: number[] | number; comment?: string },
  });

  const { isQuestionnaireArchived } =
    useIsQuestionnaireArchived({
      archiveDate: questionnaire?.archiveDate,
      badges: workflow?.badges,
    });


  useEffect(() => {
    if (!user || readOnly || !questionnaire) return;
    if (user.type !== USER_TYPES.VENDOR_ADMIN && questionnaire.type === QuestionnaireTypes.MPA_BEST_PRACTICE) dispatch(setReadOnly(true));
  }, [user, questionnaire]);

  useEffect(() => {
    if (!isQuestionnaireArchived) return;

    dispatch(setReadOnly(true));
  }, [isQuestionnaireArchived]);

  const [certificationControl, setCertificationControl] = useState<CertificationControl | undefined>(undefined);
  const [curQuestionAnswer, setCurQuestionAnswer] = useState<QuestionAnswer | undefined>(undefined);
  const [subRemVFiles, setSubRemVFiles] = useState<{ [key: string]: FileUpload[] }>({});
  const [subRemNVFiles, setSubRemNVFiles] = useState<{ [key: string]: FileUpload[] }>({});
  const [expanded, setExpanded] = useState<boolean>(true);
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [nfiles, setNFiles] = useState<FileUpload[]>([]);
  const [vfiles, setvFiles] = useState<FileUpload[]>([]);
  const [nvfiles, setNvFiles] = useState<FileUpload[]>([]);
  const [comments, setComments] = useState<Comment[] | undefined>(undefined);
  const [lastUpdated, setLastUpdated] = useState<AuditLog[] | undefined>(undefined);
  const [color, setColor] = useState<PaletteKey>(() => {
    const { question: currentQuestion, relavantQuestionStatus } = props;
    if (currentQuestion && relavantQuestionStatus?.status) {
      const status = relavantQuestionStatus.status;
      if (assessmentQuestionsStatus && !!assessmentQuestionsStatus[currentQuestion.id]?.vendorStatus) {
        return assessmentQuestionsStatus[currentQuestion.id]?.verificationStatus === REMEDIATION_VERIFICATION_STATUS.reviewed || (
          assessmentQuestionsStatus[currentQuestion.id]?.remediationQuestionAnswers?.length && assessmentQuestionsStatus[currentQuestion.id]?.remediationQuestionAnswers?.every(rqa => rqa.verificationStatus === REMEDIATION_VERIFICATION_STATUS.reviewed)
        ) ? 'primary' : 'nonCompliant';
      }
      return ColorMap[status] || 'info';
    }
    return 'info';
  });
  const [inclusion, setInclusion] = useState<InclusionConfiguration | undefined>(undefined);
  const [waitForNext, setWaitForNext] = useState(false);
  const [assessorFinding, setAssessorFinding] = useState('');
  const [assessorAnswer, setAssessorAnswer] = useState('');
  const [vendorStatus, setVendorStatus] = useState('');
  const [willNotRemediateReason, setWillNotRemediateReason] = useState<WillNotRemediateReasons | undefined>(undefined);
  const [remediationComment, setRemediationComment] = useState('');
  const [remediationDate, setRemediationDate] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [auditLogKill, setAuditLogKill] = useState(false);
  const [isAssessmentFindingsOpen, setIsAssessmentFindingsOpen] = useState(false);
  const [isSavingFindings, setIsSavingFindings] = useState(false);
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);
  const [subRemErr, setSubRemErr] = useState<string>('');
  const [remediationDateErr, setRemediationDateErr] = useState<string>('');
  const [stepIndex, setStepIndex] = React.useState<number>(0);
  const [bpComplete, setBPCompleteOpen] = useState(false);

  const handleBPCompleteOpen = () => {
    setBPCompleteOpen(true);
  };

  const handleBPCompleteClose = () => setBPCompleteOpen(false);
  const [commentModal, setCommentModalOpen] = useState(false);
  const handleCommentModalOpen = () => setCommentModalOpen(true);
  const handleCommentModalClose = () => setCommentModalOpen(false);
  const [auditLogModal, setAuditLogModalOpen] = useState(false);
  const handleAuditLogModalOpen = () => setAuditLogModalOpen(true);
  const handleAuditLogModalClose = () => setAuditLogModalOpen(false);
  const [showSavingCircle, setShowSavingCircle] = useState(false);
  const [assessorFindingModal, setAssessorFindingModalOpen] = useState(false);
  const handleAssessorFindingModalOpen = () => setAssessorFindingModalOpen(true);
  const [subRemediations, setSubRemediations] = useState<SubRemediation[]>([]);

  const needsVerification = useMemo(() => {
    if (!workflow || !question.bestPractice) return false;

    const isWfFiveThreeOneOrGreater = isWorkflowFiveThreeOneOrGreater(workflow.workflowType);

    if (isWfFiveThreeOneOrGreater) return true;

    const bpCpy = question.bestPractice;
    return typeof bpCpy === 'object' &&
      bpVerificationList?.some(bp => bp.bestpracticeId === bpCpy.id && question.isBestPractice) || false;
  }, [bpVerificationList, question, workflow]);

  const setSubRemediationFromAssessorQuestionAnswers = useCallback(() => {
    if (!assessmentQuestionsStatus || !question.id || !assessmentQuestionsStatus[question.id]) return;
    const { assessorQuestionAnswers, remediationQuestionAnswers } = assessmentQuestionsStatus[question.id];
    if (remediationQuestionAnswers && remediationQuestionAnswers.length > 0) {
      const subRems = [...remediationQuestionAnswers].map((remediation) => ({
        id: remediation.id,
        assessorAnswer: remediation.assessorAnswer,
        remediationPlan: remediation.remediationPlan,
        remediationComment: remediation.remediationComment,
        remediationDate: remediation.remediationDate,
        willNotRemediateReason: remediation.willNotRemediateReason,
        answer: typeof remediation.answer === 'number' ? remediation.answer : remediation.answer.id,
        text: typeof remediation.answer !== 'number' ? '' : question.answers.find((answer) => answer.id === remediation.answer)?.text,
        attachments: remediation.attachments,
      }));

      subRems.sort((a, b) => {
        const answerA = typeof a.answer === 'number' ? a.answer : !!a.answer ? a.answer.id : 0;
        const answerB = typeof b.answer === 'number' ? b.answer : !!b.answer ? b.answer.id : 0;
        return answerA - answerB;
      });
      setSubRemediations(subRems);
      return;
    }

    const tmpSubRemediations = assessorQuestionAnswers?.reduce((acc, it) => {
      // Only show sub-remediations that are not implemented or not applicable
      if (!it.compensatingOrNotApplicable || ['implemented', 'not_applicable'].includes(it.compensatingOrNotApplicable || '')) return acc;
      acc.push({
        answer: it.answer as number,
        text: question.answers.find((answer) => answer.id === it.answer)?.text,
        remediationPlan: '',
        remediationComment: '',
        remediationDate: new Date().toISOString(),
        assessorAnswer: it.assessorAnswer as number,
      });
      return acc;
    }, [] as SubRemediation[]);

    if (tmpSubRemediations?.length) {
      tmpSubRemediations.sort((a, b) => {
        const answerA = typeof a.answer === 'number' ? a.answer : !!a.answer ? a.answer.id : 0;
        const answerB = typeof b.answer === 'number' ? b.answer : !!b.answer ? b.answer.id : 0;
        return answerA - answerB;
      });
    }

    const dedupedTmp = (tmpSubRemediations || []).reduce((acc: SubRemediation[], curr) => {
      const answerId = typeof curr.answer === 'number' ? curr.answer : (curr.answer as any)?.id;
      if (!acc.some((s) => (typeof s.answer === 'number' ? s.answer : (s.answer as any)?.id) === answerId)) {
        acc.push(curr);
      }
      return acc;
    }, []);

    setSubRemediations(dedupedTmp || []);
  }, [assessmentQuestionsStatus, question.id]);

  const resetSelectedCtrlAns = useCallback((resetFromStore: boolean) => {
    if (!assessmentQuestionsStatus || !question.id || !assessmentQuestionsStatus[question.id]) {
      setSelectedCtrlAns({});
      return;
    }

    const { assessorQuestionAnswers } = assessmentQuestionsStatus[question.id];
    if (!assessorQuestionAnswers || assessorQuestionAnswers.length === 0) {
      setSelectedCtrlAns({});
      return;
    }
    if (resetFromStore) {
      setSelectedCtrlAns([...assessorQuestionAnswers].reduce((acc: { [key: string]: PIDetails }, curr) => {
        if (acc[curr.answer as number] !== undefined) return acc;
        acc[curr.answer as number] = {
          compensatingOrNotApplicable: curr.compensatingOrNotApplicable,
          isImplemented: curr.isImplemented,
          comment: curr.comment,
          id: curr.id,
          answer: curr.answer,
        };
        return acc;
      }, {}));
    }
  }, [assessmentQuestionsStatus, question.id]);

  const resetAssessorAnswerAndFinding = useCallback(() => {
    setAssessorAnswer((assessmentQuestionsStatus && assessmentQuestionsStatus[question.id] && assessmentQuestionsStatus[question.id]?.answer) || '');
    setAssessorFinding((assessmentQuestionsStatus && assessmentQuestionsStatus[question.id] && assessmentQuestionsStatus[question.id]?.assessorFinding) || '');
  }, [assessmentQuestionsStatus, question.id]);

  const handleAssessorFindingModalClose = useCallback((resetFromStore: boolean) => {
    setErrorMessage('');
    if (resetFromStore) {
      resetAssessorAnswerAndFinding();
    }
    setAssessorFindingModalOpen(false);
    resetSelectedCtrlAns(resetFromStore);
  }, [resetAssessorAnswerAndFinding, resetSelectedCtrlAns]);

  const handleAssessorFindingsClose = useCallback((resetFromStore: boolean) => {
    setErrorMessage('');
    if (resetFromStore) {
      resetAssessorAnswerAndFinding();
    }
    setIsAssessmentFindingsOpen(false);
  }, [resetAssessorAnswerAndFinding]);

  const [remediationModal, setRemediationModalOpen] = useState(false);
  const handleRemediationModalOpen = () => setRemediationModalOpen(true);
  const handleRemediationModalClose = () => {
    setSubRemediationFromAssessorQuestionAnswers();
    setStepIndex(0);
    setSubRemNVFiles({});
    setRemediationModalOpen(false);
  };
  const [attachmentModal, setAttachmentModalOpen] = useState(false);
  const [qIdsFetched, setQIdsFetched] = useState<{
    [key: number]: boolean;
  }>({});
  const handleAttachmentModalOpen = () => {
    setNFiles(files);
    setAttachmentModalOpen(true);
  };
  const handleAttachmentModalClose = () => setAttachmentModalOpen(false);
  const [newSurveyId, setnewSurveyId] = useState<number | undefined>(undefined);
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(false);
  //const handleUploadDocumentOpen = () => setUploadDocumentOpen(true);
  const handleUploadDocumentClose = () => setUploadDocumentOpen(false);
  const [certUploadedOpen, setCertUploadedOpen] = useState(false);
  const handleCertUploadedOpen = () => setCertUploadedOpen(true);
  const handleCertUploadedClose = () => setCertUploadedOpen(false);
  const siteOrApplicationName = useMemo(() => {
    if (!company || !survey || (!survey.application && !survey.site)) return '';

    const site = survey.site;
    const application = survey.application;

    return site ?
      company.locations?.find(loc => loc.id === site)?.name :
      company.companyApplications?.find(app => app.application.id === application)?.application.name || '';
  }, [company, survey]);

  const handleVendorStatusChange = (newVendorStatus: string) => {
    setErrorMessage('');
    setVendorStatus(newVendorStatus);
  };

  const handleSetSubRemNVFile = useCallback((tfiles: { [key: string]: FileUpload[] }) => {
    setSubRemNVFiles({ ...subRemNVFiles, ...tfiles });
    setSubRemVFiles({ ...subRemVFiles, ...tfiles });
  }, [subRemNVFiles, subRemVFiles]);

  const handleSetNVFile = (tfiles: FileUpload[]) => {
    const newFiles = tfiles.flat(1);
    setNvFiles(newFiles);
  };

  const handleSetNFiles = (tfiles: FileUpload[]) => {
    const newFiles = tfiles.flat(1);
    setNFiles(newFiles);
  };

  const processGroupAnswers = useCallback(
    async (questionsNeedingCertAnswers: QuestionsNeedingCertAnswersMap) => {
      if (!questionnaire || !survey || !question) return;

      const certTitles = new Set<string>();
      const arQCodes = new Set<string>();
      const bpQCodes = new Set<string>();
      const bpByCode: { [key: string]: BestPractice } = {};
      for (const entry of Object.values(questionsNeedingCertAnswers)) {
        entry.bpCodes.forEach(({ code, bestPractice: locBp }) => {
          bpQCodes.add(code);
          bpByCode[code] = locBp;
        });
        entry.arCodes.forEach(({ code, bestPractice: locBp }) => {
          arQCodes.add(code);
          bpByCode[code] = locBp;
        });
        entry.certificates.forEach((cert) => {
          certTitles.add(cert.title);
        });
      }

      if (!workflow) return;
      const recordIds = Object.keys(questionsNeedingCertAnswers);
      const generatedIds = certAnswersGeneratedByWorkflow.get(workflow.id);
      if (recordIds.length && generatedIds && recordIds.every((id) => generatedIds.has(id))) return;

      if (certAnswerGenerationInFlight.has(workflow.id)) return;
      certAnswerGenerationInFlight.add(workflow.id);

      try {
        const certAnswers = await createCertificateBasedQuestionAnswers(workflow.id);

        certAnswers.forEach((qa) => {
          dispatch(setQuestionAnswer(String(qa.question), qa, workflow.workflowType));
        });
        const generated = certAnswersGeneratedByWorkflow.get(workflow.id) ?? new Set<string>();
        recordIds.forEach((id) => generated.add(id));
        certAnswersGeneratedByWorkflow.set(workflow.id, generated);

        if (!companyCerts) return;

        dispatch(
          setAnsweredByCertModalData({
            titles: Array.from(certTitles),
            arCodes: Array.from(arQCodes).map((code) => ({
              code,
              bestPractice: bpByCode[code],
            })),
            bpCodes: Array.from(bpQCodes).map((code) => ({
              code,
              bestPractice: bpByCode[code],
            })),
          }),
        );
      } catch (error) {
        console.error('Error creating certificate based question answers:', error);
      } finally {
        certAnswerGenerationInFlight.delete(workflow.id);
      }
    },
    [questionnaire?.id, survey?.id, question.id, workflow, companyCerts, dispatch],
  );

  useEffect(() => {
    if (
      !allComments ||
      !assessmentQuestionsStatus ||
      !assessmentQuestionsStatus[question.id] ||
      assessmentQuestionsStatus[question.id].comments?.length === 0 ||
      (comments && comments.length === assessmentQuestionsStatus[question.id].comments?.length)
    )
      return;
    const newComments: Comment[] = [];
    const commentsToFetch: number[] = [];
    assessmentQuestionsStatus[question.id].comments?.forEach((comment) => {
      const commentId = typeof comment === 'number' ? comment : comment.id;
      if (!commentId) return;
      if (allComments[commentId]) {
        newComments.push(allComments[commentId]);
      } else {
        commentsToFetch.push(commentId);
      }
    });
    setComments(newComments);
    if (commentsToFetch.length) {
      getMultipleComments(commentsToFetch).then((fetchedComments) => dispatch(setStoreComments(fetchedComments)));
    }
  }, [allComments, assessmentQuestionsStatus]);

  useEffect(() => {
    if (!questionStatus) return;
    const curQStatus = questionStatus[question.id];
    if (!curQStatus || !curQStatus.questionAnswer) return;

    const handleGetAuditLogByQuestionAnswerId = async (qId: any) => {
      const logs = await getAuditLogByQuestionAnswerId(qId);
      dispatch(setQuestionAuditLogFetched(question.id, workflow?.workflowType));
      dispatch(setAuditLogs(logs));
    };
    if (!curQStatus.auditLogsFetched || !auditLogKill && !qIdsFetched[question.id]) {
      setQIdsFetched((prev) => {
        if (prev[question.id]) return prev;
        return { ...prev, [question.id]: true };
      });
      setAuditLogKill(true);
      if (!qIdsFetched[question.id]) {
        setQIdsFetched((prev) => ({ ...prev, [question.id]: true }));
        handleGetAuditLogByQuestionAnswerId(curQStatus.questionAnswer.id);
      }
    } else {
      if (!auditLogs) return;
      const newAuditLogs: AuditLog[] = lastUpdated ? [...lastUpdated] : [];

      Object.keys(auditLogs).forEach((log) => {
        if (
          auditLogs[log].questionAnswer === curQStatus.questionAnswer?.id &&
          (!lastUpdated || lastUpdated.findIndex((lu) => lu.id === auditLogs[log].id) === -1)
        )
          newAuditLogs.push(auditLogs[log]);
      });

      setLastUpdated((prev) => {
        if (
          prev &&
          newAuditLogs.every((log, i) => prev[i]?.id === log.id) &&
          newAuditLogs.length === prev.length
        ) {
          return prev;
        }

        return newAuditLogs.sort((a, b) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime());
      });
    }
  }, [auditLogs, questionStatus]);

  const primeSelectedCtrlAns = useCallback(() => {
    setSelectedCtrlAns(
      question.answers.filter(it => !['Fully Implemented', 'Not Implemented', 'Not Applicable'].includes(it.text || '')).reduce((acc: { [key: string]: PIDetails }, curr) => {
        acc[curr.id] = {
          compensatingOrNotApplicable: '',
          isImplemented: false,
          comment: '',
          answer: curr.id,
        };
        return acc;
      }, {}),
    );
  }, [question.answers]);

  useEffect(() => {
    if (!hasNeedsCertQARecords || user?.type !== USER_TYPES.VENDOR_ADMIN) return;

    processGroupAnswers(needsCertQARecords);
  }, [hasNeedsCertQARecords, needsCertQARecords, user?.type]);

  useEffect(() => {
    if (
      !auditLogs ||
      !assessmentQuestionsStatus ||
      !assessmentQuestionsStatus[question.id] ||
      assessmentQuestionsStatus[question.id].auditLogEntries?.length === 0 ||
      (lastUpdated && assessmentQuestionsStatus[question.id].auditLogEntries?.length === lastUpdated.length)
    )
      return;
    const newAuditLogs: AuditLog[] = lastUpdated ? [...lastUpdated] : [];

    assessmentQuestionsStatus[question.id].auditLogEntries?.forEach((log) => {
      const logId = typeof log === 'number' ? log : log.id;
      if (!logId) return;
      if (auditLogs[logId] && (!lastUpdated || lastUpdated.findIndex((lu) => lu.id === logId) === -1))
        newAuditLogs.push(auditLogs[logId]);
    });
    setLastUpdated(
      newAuditLogs.sort((a, b) => {
        if (a.createdAt! < b.createdAt!) return 1;
        if (a.createdAt! > b.createdAt!) return -1;
        return 0;
      }),
    );
  }, [auditLogs, assessmentQuestionsStatus]);

  useEffect(() => {
    if (!questionStatus) return;
    const curQStatus = questionStatus[question.id];

    const evidenceids: number[] = [];
    const existingEvidence = Object.keys(evidenceById);
    if (curQStatus && curQStatus.questionAnswer && curQStatus.questionAnswer.attachments) {
      curQStatus.questionAnswer.attachments.forEach((ev) => {
        if (existingEvidence.indexOf(ev.toString()) === -1) evidenceids.push(ev);
      });
    }

    if (
      assessmentQuestionsStatus &&
      assessmentQuestionsStatus[question.id] &&
      assessmentQuestionsStatus[question.id].attachments
    ) {
      assessmentQuestionsStatus[question.id].attachments?.forEach((ev) => {
        if (existingEvidence.indexOf(ev.toString()) === -1) evidenceids.push(ev);
      });
    }

    if (evidenceids.length > 0) {
      const formattedEvidenceIds = evidenceids.map((ev) => typeof ev === 'number' ? ev : (ev as Evidence).id!);
      getManyEvidence(formattedEvidenceIds).then((nev) => {
        let shouldSetEvidence = false;
        nev.forEach(e => {
          if (evidenceById[e.id!] === undefined) shouldSetEvidence = true;
        });
        if (shouldSetEvidence) dispatch(setEvidence(nev));
      });
    }

    if (
      curQStatus &&
      curQStatus.questionAnswer &&
      curQStatus.questionAnswer.attachments &&
      files.length !== curQStatus.questionAnswer.attachments.length
    ) {
      const newFileList: FileUpload[] = [];
      curQStatus.questionAnswer.attachments.forEach((ev) => {
        if (evidenceById[ev]) {
          // if user's content owner and evidence is NOT public, skip it
          if (user?.type === USER_TYPES.CONTENT_OWNER && !evidenceById[ev].isPublic) {
            return;
          }

          newFileList.push({
            id: evidenceById[ev].id,
            file: evidenceById[ev].file.document,
            date: evidenceById[ev].file.createdAt,
            user: evidenceById[ev].createdBy as User,
            isPublic: evidenceById[ev].isPublic,
          });
        }
      });
      setFiles(newFileList);
    }
    if (
      !assessmentQuestionsStatus ||
      !assessmentQuestionsStatus[question.id] ||
      !assessmentQuestionsStatus[question.id].attachments ||
      assessmentQuestionsStatus[question.id].attachments?.length === 0
    )
      return;
    if (vfiles.length !== assessmentQuestionsStatus[question.id].attachments?.length) {
      const newvFileList: FileUpload[] = [];
      assessmentQuestionsStatus[question.id].attachments?.forEach((ev) => {
        if (evidenceById[ev]) {
          newvFileList.push({
            id: evidenceById[ev].id,
            file: evidenceById[ev].file.document,
            date: evidenceById[ev].file.createdAt,
            user: evidenceById[ev].createdBy as User,
            isPublic: evidenceById[ev].isPublic,
          });
        }
      });
      setvFiles(newvFileList);
    }

    if (assessmentQuestionsStatus[question.id].remediationQuestionAnswers) {
      const subRemQAs = assessmentQuestionsStatus[question.id].remediationQuestionAnswers;
      const subRemediationFiles: { [key: string]: FileUpload[] } = {};
      if (!subRemQAs) return;
      subRemQAs.forEach((subRem) => {
        if (!subRem.attachments) return;
        const subRemFiles: FileUpload[] = [];
        subRem.attachments.forEach((ev: number | FileUpload) => {
          const evidence = typeof ev === 'number' ? evidenceById[ev] : evidenceById[ev.id!];
          if (evidence) {
            subRemFiles.push({
              id: evidence.id,
              file: evidence.file.document,
              date: evidence.file.createdAt,
              user: evidence.createdBy as User,
              isPublic: evidence.isPublic,
            });
          }
        });
        subRemediationFiles[
          typeof subRem.answer === 'number' ? subRem.answer.toString() : (subRem.answer.id as string)] = subRemFiles;
      });
      setSubRemVFiles(subRemediationFiles);
    }
  }, [questionStatus, evidenceById, assessmentQuestionsStatus]);

  const debounceNextQuestion = React.useRef(
    debounce(() => {
      dispatch(nextQuestion());
    }, 25),
  ).current;

  useEffect(() => {
    if (!questionItem) return;
    setExpanded(questionItem.id === question.id);
  }, [questionItem]);

  useEffect(() => {
    if (!surveys || !survey || (survey.questionnaire as Questionnaire).type === QuestionnaireTypes.MPA_BEST_PRACTICE) return;
    let surveyExists: Survey | undefined = undefined;

    if (survey.site)
      surveyExists = surveys.find(
        (sur) => (sur.questionnaire as Questionnaire).type === QuestionnaireTypes.MPA_BEST_PRACTICE && sur.site === survey.site,
      );

    if (survey.application)
      surveyExists = surveys.find(
        (sur) => (sur.questionnaire as Questionnaire).type === QuestionnaireTypes.MPA_BEST_PRACTICE && sur.application === survey.application,
      );

    if (surveyExists) setnewSurveyId(surveyExists.id);
  }, [surveys]);

  useEffect(() => {
    if (!bpStatus || !waitForNext || !questionStatus) return;
    if (Object.keys(questionStatus).find(q =>questionStatus[q].questionAnswer && !questionStatus[q].questionAnswer?.id)) return;

    setWaitForNext(false);

    if (!bestPractice || !bpStatus[bestPractice.id]) {
      debounceNextQuestion();
      return;
    }

    if (bpStatus[bestPractice.id].firstUnanswered && bpStatus[bestPractice.id].lastQuestion === question.id) {
      dispatch(setQuestion(bpStatus[bestPractice.id].firstUnanswered!));
      return;
    }

    if (bpStatus[bestPractice.id].visibleQuestionsCount === bpStatus[bestPractice.id].questionsAnsweredCount) {
      let surveyComplete = true;
      Object.keys(bpStatus).forEach((bpid) => {
        if (
          bpStatus[bpid].visibleQuestionsCount !== bpStatus[bpid].questionsAnsweredCount &&
          bpStatus[bpid].status !== 'hide'
        )
          surveyComplete = false;
      });
      if (!surveyComplete) {
        if (props.incompleteQuestionIds !== undefined) {
          dispatch(nextQuestion());
        }
        handleBPCompleteOpen();
        return;
      }
    }

    // Confirm everyControlHasAnswer on MPA_BEST_PRACTICE Questionnaires
    const isMPABestPracticeQuestionniare = questionnaire?.type === QuestionnaireTypes.MPA_BEST_PRACTICE;
    if (isFiveThreeOrGreater && isMPABestPracticeQuestionniare) {
      const questionAnswer = questionStatus[question.id]?.questionAnswer;

      const everyControlHasAnswer = questionAnswer ? everyQuestionControlHasAnswer(questionAnswer, question) : false;
      if (!everyControlHasAnswer) return;
    }

    if (isMPABestPracticeQuestionniare || (!isMPABestPracticeQuestionniare && !!questionStatus[question.id]?.questionAnswer?.answer?.length)) debounceNextQuestion();
  }, [bpStatus]);

  useEffect(() => {
    if (!question) return;
    setCurrentQuestion(props.question);

    if (
      (certControlsById && !companyCerts) ||
      !question.excludedByCertifications ||
      question.excludedByCertifications.length === 0
    )
      return;

    let certifications: CertificationControl[] = [];
    question.excludedByCertifications.forEach((cert) => {
      if (!cert) return;
      if (typeof cert === 'number' && certControlsById) {
        const ccontrol = certControlsById[cert];
        certifications.push(ccontrol);
      }
      if (typeof cert !== 'number') certifications.push(cert);
    });
    if (certifications.length > 0) setCurrentQuestion({ ...props.question, excludedByCertifications: certifications });
  }, [props.question, certControlsById]);

  useEffect(() => {
    if (!companyCerts || companyCerts.length === 0) {
      setCertificationControl(undefined);
      return;
    }
    question.excludedByCertifications.forEach((certControl) => {
      const ncertControl: CertificationControl | boolean =
        typeof certControl === 'number' && certControlsById
          ? certControlsById[certControl]
          : typeof certControl !== 'number'
            ? certControl
            : false;
      if (ncertControl && companyCerts?.find((cert) => cert.certification.id === ncertControl.certification.id))
        setCertificationControl(ncertControl);
    });
  }, [companyCerts]);

  const getBackgroundColor = theme.palette.mode === 'light' ? lighten : darken;

  const setQuestionHandler = useCallback(() => {
    if (!questionItem) {
      setExpanded(!expanded);
      return;
    }
    dispatch(setQuestion(question));
    handleAssessorFindingsClose(true);
  }, [questionItem, expanded, question, handleAssessorFindingsClose]);

  const createNewAuditLog = async (
    method: 'delete' | 'post' | 'patch',
    event: string,
    description: string,
    qaResult?: QuestionAnswer,
  ) => {
    if (!user || !survey) return;
    const questionAnswerExists =
      qaResult ||
      (questionStatus && questionStatus[question.id] && questionStatus[question.id].questionAnswer !== undefined);
    const newAuditLog = await createAuditLog({
      user,
      method,
      event,
      description,
      directLink:
        window.location.origin +
        window.location.pathname +
        (window.location.pathname[window.location.pathname.length - 1] !== '/' ? '/' : '') +
        '?question=' +
        question?.id,
      assessorAnswer:
        assessmentQuestionsStatus && assessmentQuestionsStatus[question.id]
          ? assessmentQuestionsStatus[question.id].id
          : undefined,
      questionAnswer: questionAnswerExists
        ? qaResult
          ? qaResult.id
          : questionStatus[question.id].questionAnswer?.id
        : undefined,
      assessment: assessment ? assessment.id : undefined,
      assessmentSurvey: assessment
        ? (assessment.surveys as AssessmentSurvey[]).find((asssur) =>
          typeof asssur.survey === 'number' ? asssur.survey === survey?.id : asssur.survey.id === survey?.id,
        )?.id
        : undefined,
      companies: assessment
        ? [
          typeof assessment.assessor === 'number' ? assessment.assessor : assessment.assessor.id!,
          typeof assessment.company === 'number' ? assessment.company : assessment.company.id!,
        ]
        : [typeof survey.company === 'number' ? survey.company : survey.company.id!],
    });
    return newAuditLog;
  };

  const handleUpdateAssessmentQuestion = async (prop: {
    status?: string;
    hideAlert?: boolean;
    retainAssessorStatus?: boolean;
    comment?: Comment;
    auditLog?: AuditLog;
    contentOwnerPriority?: number[];
    attachments?: number[];
    savePIQA?: boolean;
    savedSubRemFiles?: { [key: string]: FileUpload[] };
    subRemediations?: SubRemediation[];
    questionCtrlAns?: { [key: number]: PIDetails };
  }) => {
    // Set default sub remediation object automatically if 5.3 or greater and assessor answer is NOT_IMPLEMENTED
    let localQuestionCtrlAns = prop.questionCtrlAns;
    if (isFiveThreeOrGreater && assessorAnswer === NOT_IMPLEMENTED && question.type
      && questionStatus[question.id]) {
      const questionAnswer = questionStatus[question.id].questionAnswer;
      if (!questionAnswer) return;

      const defaultQuestionCtrlAns = generateDefaultSubRemediationsObject(question.answers, question.type, questionAnswer);
      if (prop.questionCtrlAns) {
        for (const aqa in prop.questionCtrlAns) {
          if (defaultQuestionCtrlAns[aqa] && prop.questionCtrlAns[aqa].id) {
            defaultQuestionCtrlAns[aqa].id = prop.questionCtrlAns[aqa].id;
          }
        }
      }

      prop.questionCtrlAns = defaultQuestionCtrlAns;
    }

    if (!assessmentQuestionsStatus) return;
    if (!prop.status && !prop.retainAssessorStatus) prop.status = curQuestionStatus;
    if (assessorAnswer && prop.status !== 'priority') {
      // Is Assessor Answer Partially Implemented on 5.3 or greater and no sub-remediations require remediation
      const isPIWithoutRemediationSubRem = assessorAnswer === 'Partially Implemented'
        && isFiveThreeOrGreater
        && prop.questionCtrlAns
        && Object.values(prop.questionCtrlAns).every((PIDetailsObj) => {
          return !['compensating_control', 'not_implemented'].includes(PIDetailsObj.compensatingOrNotApplicable || '');
        });

      if (!prop.retainAssessorStatus) {
        prop.status =
        ((['Fully Implemented', 'Not Applicable'].indexOf(assessorAnswer) !== -1 ) || isPIWithoutRemediationSubRem) ? 'assessorApproved' : 'remediation';
      }
    }
    if (prop.status === 'priority' && ['Fully Implemented', 'Not Applicable'].indexOf(assessorAnswer) !== -1)
      prop.status = 'assessorApproved';
    if (vendorStatus === REMEDIATION_OPTIONS.remediated) prop.status = 'submitted';
    if (user?.type === USER_TYPES.CONTENT_OWNER) {
      const companyId = typeof user?.companies[0] === 'number' ? user.companies[0] : user?.companies[0].id;
      if (
        companyId &&
        prop.status === 'remediation' &&
        prop.contentOwnerPriority &&
        prop.contentOwnerPriority.indexOf(companyId) !== -1
      )
        prop.status = 'priority';
      if (
        companyId &&
        prop.status === 'priority' &&
        prop.contentOwnerPriority &&
        prop.contentOwnerPriority.indexOf(companyId) === -1
      )
        prop.status = 'remediation';
    }

    const isNewAssessorAnswer = !assessmentQuestionsStatus[question.id];
    const data = ((prop.savePIQA && isNewAssessorAnswer) || prop.comment)
      ? undefined
      : await handleSaveAssessorQuestionAnswer(localQuestionCtrlAns, question.id, assessmentQuestionsStatus);

    if (prop.subRemediations) {
      // Append the sub remediation files to the sub remediation object
      const subRems = prop.subRemediations.map((subRem) => {
        const subRemFiles = prop.savedSubRemFiles && prop.savedSubRemFiles[typeof subRem.answer === 'number' ? subRem.answer : (subRem.answer.id as string)];
        if (subRemFiles) {
          subRem.attachments = subRemFiles.map((file) => file.id as number);
        }
        subRem.answer = typeof subRem.answer === 'number' ? subRem.answer : subRem.answer.id;
        subRem.assessorAnswer = typeof subRem.assessorAnswer === 'number' ? subRem.assessorAnswer : subRem.assessorAnswer.id as number;
        return subRem;
      });
      prop.subRemediations = subRems;
      if (data && data[question.id]) {
        if (data[question.id].remediationQuestionAnswers?.length) {
          // Merge existing remediationQuestionAnswers with new subRemediations
          data[question.id].remediationQuestionAnswers = data[question.id].remediationQuestionAnswers?.map((rqa) => {
            const matchingSubRem = prop.subRemediations?.find((subRem) => (
              (typeof subRem.answer === 'number' ? subRem.answer : subRem.answer.id) === (typeof rqa.answer === 'number' ? rqa.answer : rqa.answer.id)
              && (typeof subRem.assessorAnswer === 'number' ? subRem.assessorAnswer : subRem.assessorAnswer.id) === (typeof rqa.assessorAnswer === 'number' ? rqa.assessorAnswer : rqa.assessorAnswer.id)
            ));

            if (matchingSubRem) {
              return {
                ...rqa,
                remediationPlan: matchingSubRem.remediationPlan,
                remediationComment: matchingSubRem.remediationComment,
                remediationDate: !!matchingSubRem.remediationDate ? matchingSubRem.remediationDate : rqa.remediationDate,
                willNotRemediateReason: matchingSubRem.willNotRemediateReason,
                attachments: matchingSubRem.attachments as number[] | [],
              };
            }
            return rqa;
          });
        } else {
          // Temp values later updated with ids
          data[question.id].remediationQuestionAnswers = prop.subRemediations?.map((subRem) => ({
            answer: typeof subRem.answer === 'number' ? subRem.answer : subRem.answer.id,
            assessorAnswer: typeof subRem.assessorAnswer === 'number' ? subRem.assessorAnswer : subRem.assessorAnswer.id as number,
            remediationPlan: subRem.remediationPlan,
            remediationComment: subRem.remediationComment,
            remediationDate: !!subRem.remediationDate ? subRem.remediationDate : undefined,
            willNotRemediateReason: subRem.willNotRemediateReason,
            attachments: subRem.attachments as number[] | [],
          }));
        }
        dispatch(setAllAssessmentQuestionStatus(data));
      }
    } else if (data) {
      dispatch(setAllAssessmentQuestionStatus(data));
    }

    const formattedSubRemediations = prop.subRemediations?.map((subRem) => ({
      ...subRem,
      attachments: subRem.attachments?.map((file) => (typeof file === 'number' ? file : (file as File).id)),
    })) || [];

    let createdSubRemediations;

    try {
      createdSubRemediations = prop.subRemediations
        ? await handleSaveSubRemediations(formattedSubRemediations)
        : undefined;
    } catch (error) {
      setSubRemErr('Unable to save sub-remediation details. Please refresh the page and try again.');
      throw error;
    }

    if (!assessment || !survey) return;

    const surveyIndex = (assessment.surveys as AssessmentSurvey[]).findIndex(
      (assSur) => (assSur.survey as Survey).id === surveyId,
    );

    if (surveyIndex === -1) return;

    const {
      bestPracticeRemediationCount,
      implementationGuidanceRemediationCount,
    } = getRemediationCounts({
      assessmentQuestionsStatus,
      assessment,
      questionStatus,
      questions,
    });

    const existingComments = assessmentQuestionsStatus[question.id]?.comments || [];
    const existingCommentIds = existingComments.map((comment) => typeof comment === 'number' ? comment : comment.id!);
    const existingAssessorAnswer = assessorAnswer || assessmentQuestionsStatus[question.id]?.answer;
    const existingAssessorFinding = assessorFinding || assessmentQuestionsStatus[question.id]?.assessorFinding;

    const builtAssessorAnswer = buildAssessorAnswer({
      assessment,
      assessmentQuestionsStatus,
      questionCtrlAns: localQuestionCtrlAns,
      assessorAnswer: existingAssessorAnswer,
      assessorFinding: existingAssessorFinding,
      createdSubRemediations,
      data,
      existingCommentIds,
      prop,
      question,
      remediationComment,
      remediationDate,
      survey,
      vendorStatus,
      willNotRemediateReason,
    });

    const existingAssessorAnswerId = assessmentQuestionsStatus[question.id]?.id;

    if (!existingAssessorAnswerId) {
      // New assessor answer flow
      const createdAssessorAnswer = await createAssessorAnswer(builtAssessorAnswer as AssessorAnswer);
      const { assessorQuestionAnswers } = builtAssessorAnswer;
      if (!createdAssessorAnswer.id) return;

      const updatedQuestionAnswers = assessorQuestionAnswers?.reduce((acc, aqa) => {
        acc[aqa.answer as number] = {
          comment: aqa.comment || '',
          isImplemented: aqa.isImplemented || false,
          compensatingOrNotApplicable: aqa.compensatingOrNotApplicable || '',
          assessorAnswer: createdAssessorAnswer.id,
          answer: aqa.answer,
        };
        return acc;
      }, {} as { [key: string]: PIDetails });

      const updatedData = await handleSaveAssessorQuestionAnswer(
        updatedQuestionAnswers,
        question.id,
        assessmentQuestionsStatus,
      );

      // Hydrate builtAssessorAnswer with new assessorQuestionAnswerIds
      builtAssessorAnswer.assessorQuestionAnswers = builtAssessorAnswer.assessorQuestionAnswers?.map((aqa) => ({
        ...aqa,
        id: updatedData && updatedData[question.id] && updatedData[question.id].assessorQuestionAnswers
          ? updatedData[question.id].assessorQuestionAnswers?.find(
            (updatedAqa: any) => updatedAqa.answer === aqa.answer,
          )?.id
          : aqa.id,
      }));

      // Ensure new assessor answer is fully hydrated with question answer details for state update
      if (updatedData && updatedData[question.id] && updatedData[question.id].assessorQuestionAnswers) {
        updatedData[question.id] = {
          ...updatedData[question.id],
          ...builtAssessorAnswer,
          question: question.id,
        };
      }

      if (updatedData) dispatch(setAllAssessmentQuestionStatus(updatedData));

      // Hydrate createdAssessorAnswer the assessorQuestionAnswers
      createdAssessorAnswer.assessorQuestionAnswers = builtAssessorAnswer.assessorQuestionAnswers;
      dispatch(setAssessmentQuestionStatusId(createdAssessorAnswer));

      const newAnswers = [
        ...Object.keys(assessmentQuestionsStatus)
          .map((key) => assessmentQuestionsStatus[key])
          .filter((aa) => aa.id !== undefined && aa.id !== createdAssessorAnswer.id),
        createdAssessorAnswer,
      ];

      const newSurvey = {
        ...(assessment.surveys as AssessmentSurvey[])[surveyIndex],
      };

      newSurvey.assessorAnswers = newAnswers;

      if (
        newSurvey.bestPracticeRemediationCount !== bestPracticeRemediationCount ||
        newSurvey.implementationGuidanceRemediationCount !== implementationGuidanceRemediationCount
      ) {
        await updateAssessmentSurvey({
          ...newSurvey,
          bestPracticeRemediationCount,
          implementationGuidanceRemediationCount,
        });
      }

      dispatch(
        updateAssessmentAssessmentSurvey({
          ...newSurvey,
          bestPracticeRemediationCount,
          implementationGuidanceRemediationCount,
        }),
      );

      if (
        createdAssessorAnswer.auditLogEntries &&
        createdAssessorAnswer.auditLogEntries.length > 0
      ) {
        createdAssessorAnswer.auditLogEntries.forEach((auditLog) => {
          const fullAuditLog =
            typeof auditLog !== 'number'
              ? auditLog
              : builtAssessorAnswer.auditLog
                ? ([builtAssessorAnswer.auditLog] as AuditLog[]).find(
                  (alog) => alog.id === auditLog,
                )
                : false;

          if (fullAuditLog) {
            updateAuditLog({
              ...fullAuditLog,
              assessorAnswer: createdAssessorAnswer.id,
            });
          }
        });
      }

      dispatch(
        setAssessmentQuestionStatus({
          ...builtAssessorAnswer,
          id: createdAssessorAnswer.id,
        }),
      );

      // Ensure local selectedCtrlAns state is updated with new assessorQuestionAnswerIds for sub remediation management in 5.3 and greater
      if (isFiveThreeOrGreater && localQuestionCtrlAns) {
        const updatedSelectedCtrlAns = { ...localQuestionCtrlAns };
        builtAssessorAnswer.assessorQuestionAnswers?.forEach((aqa) => {
          const qId = aqa.answer as number;
          if (updatedSelectedCtrlAns[qId]) {
            updatedSelectedCtrlAns[qId].id = aqa.id;
          }
        });
        setSelectedCtrlAns(updatedSelectedCtrlAns);
      }

      return;
    } else {
      // Update existing assessor answer flow
      const currentAssessorAnswer = assessmentQuestionsStatus[question.id];

      // Hydrate assessorQuestionAnswers
      builtAssessorAnswer.assessorQuestionAnswers =
        builtAssessorAnswer.assessorQuestionAnswers?.length > 0 ?
          [...builtAssessorAnswer.assessorQuestionAnswers] :
          currentAssessorAnswer?.assessorQuestionAnswers ?
            [...currentAssessorAnswer.assessorQuestionAnswers] : [];

      // Hydrate existing status
      builtAssessorAnswer.status = prop.status || currentAssessorAnswer?.status || builtAssessorAnswer.status;

      const updatedAssessorAnswer = {
        ...builtAssessorAnswer,
        id: existingAssessorAnswerId,
        question: question.id,
        comments: builtAssessorAnswer.comment ? [
          builtAssessorAnswer.comment,
          ...(currentAssessorAnswer?.comments || []),
        ] : currentAssessorAnswer?.comments || [],
        attachments: (builtAssessorAnswer.attachments ?? []).map((att) =>
          typeof att === 'number' ? att : (att as FileUpload).id!,
        ),
      } as AssessorAnswer;

      await updateAssessorAnswer(updatedAssessorAnswer);
      dispatch(updateAssessmentSurveyArrayAssessorAnswer(updatedAssessorAnswer));

      const notificationToRemove = assessment?.notifications?.find(
        (notification) =>
          notification.type === REMEDIATION_PLAN_OVERDUE &&
          notification.question === updatedAssessorAnswer.question &&
          (moment(updatedAssessorAnswer.remediationDate).isSameOrAfter(moment().startOf('day')) ||
            updatedAssessorAnswer.vendorStatus !== REMEDIATION_OPTIONS.willRemediateLater),
      );

      if (notificationToRemove && notificationToRemove.id) {
        deleteAssessmentNotification(notificationToRemove.id).then(() => {
          dispatch(storeFetchAssessment(assessmentId));
        });
      }

      const newSurvey = { ...(assessment.surveys as AssessmentSurvey[])[surveyIndex] };
      if (
        newSurvey.bestPracticeRemediationCount !== bestPracticeRemediationCount ||
        newSurvey.implementationGuidanceRemediationCount !== implementationGuidanceRemediationCount
      ) {
        await updateAssessmentSurvey({ ...newSurvey, bestPracticeRemediationCount, implementationGuidanceRemediationCount });
      }

      dispatch(updateAssessmentAssessmentSurvey({ ...newSurvey, bestPracticeRemediationCount, implementationGuidanceRemediationCount }));
      dispatch(setAssessmentQuestionStatus({ ...builtAssessorAnswer, id: existingAssessorAnswerId }));
    }

    // Process complete - show alert if user is assessor and alert is not hidden
    if (user?.type === USER_TYPES.ASSESSOR && !prop.hideAlert) dispatch(setShowAssessorFindingsAlert(true));
  };

  const [baseSelectedCtrlAns, setBaseSelectedCtrlAns] = useState<{ [key: string]: PIDetails }>({});

  const handleUpdateAssessorAnswer = async (savePIQA: boolean) => {
    if (!assessmentQuestionsStatus || !user) return;
    if (!assessorAnswer || assessorFinding.length === 0) {
      setErrorMessage('Assessor Findings are required');
      return;
    }

    if (assessorAnswer === PARTIALLY_IMPLEMENTED && isFiveThreeOrGreater) {
      const hasMissingPIDetails = Object.values(baseSelectedCtrlAns).some(
        (value) => !value.compensatingOrNotApplicable,
      );

      if (hasMissingPIDetails) {
        setErrorMessage('Please provide an answer for all Partially Implemented controls.');
        return;
      }
    }

    setErrorMessage('');

    if (assessorAnswer === PARTIALLY_IMPLEMENTED && isFiveThreeOrGreater) {
      const hasMissingPIDetails = Object.values(baseSelectedCtrlAns).some(
        (value) => !value.compensatingOrNotApplicable,
      );

      if (hasMissingPIDetails) {
        setErrorMessage('Please provide an answer for all Partially Implemented controls.');
        return;
      }
    }

    setErrorMessage('');
    const curQuestion = assessmentQuestionsStatus[question.id];
    const method = !curQuestion ? 'post' : 'patch';
    let description = 'Assessor Answer and Finding added';
    if (method === 'patch') {
      if (assessorAnswer !== curQuestion.answer && assessorFinding !== curQuestion.assessorFinding) {
        description = 'Assessor Answer and Finding updated';
      } else {
        description = assessorAnswer !== curQuestion.answer ? 'Assessor Answer updated' : 'Assessor Finding updated';
      }
    }
    try {
      const auditLog = await createNewAuditLog(method, 'Assessor Answer', description);
      await handleUpdateAssessmentQuestion({ auditLog, questionCtrlAns: baseSelectedCtrlAns, savePIQA });
      handleAssessorFindingModalClose(false);
      handleAssessorFindingsClose(false);
    } catch (error) {
      console.error('Error updating assessor answer:', error);
      setErrorMessage('An error occurred while saving. Please try again.');
    }
  };

  const handleSaveFindingsClick = useCallback(async (savePIQA: boolean) => {
    try {
      setIsSavingFindings(true);
      await handleUpdateAssessorAnswer(savePIQA);
    } finally {
      setIsSavingFindings(false);
    }
  }, [handleUpdateAssessorAnswer]);

  const handleUpdateState = (qAnswer: QuestionAnswer) => {
    dispatch(setQuestionAnswer(question.id, qAnswer, workflow?.workflowType));
  };

  const watchedValues = watch(['compensatingControlAnswers', 'nonApplicableAnswers', 'nonImplementedAnswers']);
  useEffect(() => {
    if (!questionStatus || !questionStatus[question.id]) return;
    const curQStatus = questionStatus[question.id];
    if (curQStatus.status === 'hide' && !props.relavantQuestionStatus?.questionAnswer) setCurStatus(curQStatus.status);
    if (!shouldUseAssessmentColors(assessmentStatus)) {
      setCurStatus(curQStatus.status);
      if (curQStatus.status === 'incomplete') setColor('info');
      if (curQStatus.status === 'complete') setColor('success');
      if (curQStatus.status === 'remediation') setColor('error');
      if (curQStatus.status === 'submitted') setColor('warning');
      if (curQStatus.status === 'cert') setColor('primary');
      if (curQStatus.status === 'priority') setColor('secondary');
      if (curQStatus.status === 'non-compliant') setColor('nonCompliant');
    }
    if (curQStatus.satisfiedInclusions) {
      const newSatInc = curQStatus.satisfiedInclusions?.filter((data, idx) => idx === 0);
      const newInc = newSatInc ? question.inclusions.find((inc) => inc.id === newSatInc[0]) : undefined;
      setInclusion(newInc ? newInc : undefined);
    } else {
      setInclusion(undefined);
    }
    if (!curQStatus.questionAnswer || (curQuestionAnswer && isEqual(curQuestionAnswer, curQStatus.questionAnswer)))
      return;
    setCurQuestionAnswer(curQStatus.questionAnswer);
    // Set values on question selection change
    if (curQStatus.questionAnswer.compensatingControlAnswers) {
      const ccAnswer = curQStatus.questionAnswer.compensatingControlAnswers;
      if (question.type === 'multi_select') setValue('compensatingControlAnswers', ccAnswer);
      if (question.type === 'single_select') setValue('compensatingControlAnswers', ccAnswer[0]);
    }
    if (curQStatus.questionAnswer.nonApplicableAnswers) {
      const naAnswer = curQStatus.questionAnswer.nonApplicableAnswers;
      if (question.type === 'multi_select') setValue('nonApplicableAnswers', naAnswer);
      if (question.type === 'single_select') setValue('nonApplicableAnswers', naAnswer[0]);
    }
    if (curQStatus.questionAnswer.nonImplementedAnswers) {
      const niAnswer = curQStatus.questionAnswer.nonImplementedAnswers;
      if (question.type === 'multi_select') setValue('nonImplementedAnswers', niAnswer);
      if (question.type === 'single_select') setValue('nonImplementedAnswers', niAnswer[0]);
    }
    if (curQStatus.questionAnswer?.answer) {
      const answer = curQStatus.questionAnswer.answer;
      if (question.type === 'single_select') setValue('answer', answer[0]);
      if (question.type === 'multi_select') setValue('answer', answer);
    }

    if (curQStatus.questionAnswer?.text) setValue('text', curQStatus.questionAnswer.text);
    if (curQStatus.questionAnswer?.comment) setValue('comment', curQStatus.questionAnswer.comment);
    if (!preview && user && user.type !== USER_TYPES.VENDOR_ADMIN && questionnaire && questionnaire.type !== QuestionnaireTypes.MPA_BEST_PRACTICE)
      return;
    if (!curQStatus.questionAnswer || !curQStatus.questionAnswer.status) return;
    let status = '';
    try {
      status = JSON.parse(curQStatus.questionAnswer.status).status;
    } catch (e) {
      status = curQStatus.questionAnswer.status;
    }
    if (status !== curQStatus.status && certControlsById) {
      status = curQStatus.status;
      if (preview || (assessmentStatus && assessmentStatus !== 'Pre-Assessment')) {
        handleUpdateState({ ...curQStatus.questionAnswer, status });
      } else {
        let qAnswer = {
          compensatingControlAnswers: watchedValues[0] || getValues('compensatingControlAnswers') || [],
          nonApplicableAnswers: watchedValues[1] || getValues('nonApplicableAnswers') || [],
          nonImplementedAnswers: watchedValues[2] || getValues('nonImplementedAnswers') || [],
        } as QuestionAnswer;

        const req = {
          ...{ ...curQStatus.questionAnswer }, // Shallow clone
          ...qAnswer,
          status,
        };

        if (user?.type !== USER_TYPES.VENDOR_ADMIN) return;

        updateQuestionAnswer(req).then((qaResult: QuestionAnswer) => {
          handleUpdateState(qaResult);
          setCurQuestionAnswer(qaResult);
        });
      }
    }
  }, [questionStatus]);

  useEffect(() => {
    const currentQuestionStatus = questionStatus[question.id];
  
    const isUnassessed =
      !assessmentQuestionsStatus || !assessmentQuestionsStatus[question.id];

    if (isUnassessed) {
      // Exit early with default color if wf status is assigned
      if (workflow?.status === WORKFLOW_STATUSES.ASSIGNED) return;
      setColor(
        currentQuestionStatus?.status !== 'cert'
          ? 'info'
          : 'primary',
      );
      return;
    }
    const curQStatus = assessmentQuestionsStatus[question.id];
    setIsValidCORemPlan(!(assessmentQuestionsStatus[question.id]?.vendorStatus === REMEDIATION_OPTIONS.remediated));
    if (curQStatus.status) {
      setCurStatus(currentQuestionStatus.status === 'hide' ? currentQuestionStatus.status : curQStatus.status);
      if (curQStatus.status === 'remediation' || curQStatus.status === 'submitted') {
        if (!curQStatus.vendorStatus) {
          setColor('error');
        } else if (curQStatus.vendorStatus === REMEDIATION_OPTIONS.remediated &&
          (curQStatus.verificationStatus === REMEDIATION_VERIFICATION_STATUS.reviewed || curQStatus.remediationQuestionAnswers?.every(rqa => rqa.verificationStatus === REMEDIATION_VERIFICATION_STATUS.reviewed))) {
          setColor('primary');
        } else {
          setColor('nonCompliant');
        }
      }
      if (curQStatus.status === 'priority') setColor('secondary');
      if (curQStatus.status === 'assessorApproved') setColor('success');
    }

    if (curQStatus.assessorFinding) setAssessorFinding(curQStatus.assessorFinding);
    if (curQStatus.answer) setAssessorAnswer(curQStatus.answer);
    if (curQStatus.vendorStatus) setVendorStatus(curQStatus.vendorStatus);
    if (curQStatus.remediationComment) setRemediationComment(curQStatus.remediationComment);
    if (curQStatus.willNotRemediateReason) setWillNotRemediateReason(curQStatus.willNotRemediateReason);
    if (curQStatus.remediationDate) setRemediationDate(moment(curQStatus.remediationDate).format('MM/DD/yyyy'));
  }, [assessmentQuestionsStatus, assessmentQuestionsStatus?.[question.id]?.remediationQuestionAnswers]);

  const fetchQuestionRelatedInclusions = useCallback(async (questionId: number, newAnswers: number[], oldAnswers:number[]) => {
    if (!questionId) return;

    try {
      const questionInclusions = await fetchIncludedInLogic(questionId);
      if (questionInclusions) {
        const newCodes = newlyRevealedQuestionCodes(
          questionInclusions,
          newAnswers,
          oldAnswers,
        );

        dispatch(
          setNewQuestionInclusionAlert({ questionId: questionId.toString(), visible: !!newCodes.length ? true : false }),
        );

        if (!workflow || workflow.status !== WORKFLOW_STATUSES.PRE_ASSESSMENT) return;
        dispatch(updateWorkflow({ id: workflow.id, status: WORKFLOW_STATUSES.PRE_ASSESSMENT_INCOMPLETE }));
      }
    } catch (err) {
      console.error('Error fetching question inclusions:', err);
    }
  }, [workflow]);

  const submitAnswer = async (values: FieldValues) => {
    if (!questionnaire || !survey) return;

    // If Baseline quesiton, fetch question related inclusions for alerts
    if (workflow
      && !!workflow.bpSurvey
      && (questionnaire.type !== QuestionnaireTypes.MPA_BEST_PRACTICE)
      && [...PRE_ASSESSMENT_STATUSES, WORKFLOW_STATUSES.COMPLETE, WORKFLOW_STATUSES.INCOMPLETE, WORKFLOW_STATUSES.NON_COMPLIANT].includes(workflow.status as WorkflowStatus)
    ) {
      fetchQuestionRelatedInclusions(question.id, values.answer, questionStatus[question.id]?.questionAnswer?.answer || []);
    }

    setWaitForNext(true);
    let qAnswer = {
      ...buildAnswerPayload(values),
      question: question.id,
      questionnaire: questionnaire.id,
      survey: survey.id,
    } as QuestionAnswer;

    if (qAnswer.comment === '') {
      qAnswer.comment = null;
    }
    if (curQuestionAnswer) qAnswer = { ...curQuestionAnswer, ...qAnswer };
    if (
      preview ||
      (assessmentStatus && assessmentStatus !== 'Pre-Assessment') ||
      (curQuestionAnswer && isEqual(curQuestionAnswer, qAnswer))
    ) {
      handleUpdateState(qAnswer);
      return;
    }
    if (!survey || readOnly || !user || (user.type !== USER_TYPES.VENDOR_ADMIN && questionnaire?.type === QuestionnaireTypes.MPA_BEST_PRACTICE))
      return;

    setIsSavingAnswer(true);
    const newLastUpdated: AuditLog[] = lastUpdated ? [...lastUpdated] : [];
    if (questionStatus[question.id].questionAnswer) {
      let status = '';
      if ((questionStatus[question.id].questionAnswer as QuestionAnswer).status) {
        try {
          status = JSON.parse((questionStatus[question.id].questionAnswer as QuestionAnswer).status as string);
        } catch (e) {
          status = (questionStatus[question.id].questionAnswer as QuestionAnswer).status as string;
        }
      }
      createNewAuditLog('patch', 'Answer', 'Update Question Answer').then((log) => {
        if (!log) return;
        dispatch(setAuditLogs([log]));
        newLastUpdated.unshift(log);
        setLastUpdated(newLastUpdated);
      });

      const req = {
        ...{ ...questionStatus[question.id].questionAnswer }, // Shallow clone
        ...qAnswer,
        status,
      };

      updateQuestionAnswer(req).then(
        (qaResult: QuestionAnswer) => {
          handleUpdateState(qaResult);
          setCurQuestionAnswer(qaResult);
          // Refetch baseline if in pre-assessment
          if ([...PRE_ASSESSMENT_STATUSES, WORKFLOW_STATUSES.COMPLETE].includes(workflow?.status as WorkflowStatus) && workflow?.baseline && questionnaire?.type !== QuestionnaireTypes.MPA_BEST_PRACTICE) {
            dispatch(fetchBaselineSurvey({ id: workflow.baseline }));
          }
        },
      ).catch((err) => {
        console.error('Error updating question answer:', err);
      }).finally(() => {
        setIsSavingAnswer(false);
      });
    } else {
      createQuestionAnswer({ ...qAnswer }).then((qaResult: QuestionAnswer) => {
        setCurQuestionAnswer(qaResult);
        createNewAuditLog('post', 'Answer', 'Create Question Answer', qaResult).then((log) => {
          if (!log) return;
          dispatch(setAuditLogs([log]));
          newLastUpdated.unshift(log);
          setLastUpdated(newLastUpdated);
        });
        handleUpdateState(qaResult);
      }).catch((err) => {
        console.error('Error creating question answer:', err);
      }).finally(() => {
        setIsSavingAnswer(false);
      });
    }
  };

  const uploadAttachments = async () => {
    if (!files || !user || user.type !== USER_TYPES.VENDOR_ADMIN || !questionStatus || !survey) return;
    handleAttachmentModalClose();

    const newLastUpdated: AuditLog[] = lastUpdated ? [...lastUpdated] : [];
    const onlyNewFiles = nfiles.filter((file) => typeof file.file !== 'string');
    const existingFiles = nfiles.filter((file) => typeof file.file === 'string');
    
    let evidence = await uploadMultipleAttachments(
      onlyNewFiles,
      user,
    );
    const updatedEvidences = await updateEvidences(existingFiles);
    dispatch(setEvidence([...updatedEvidences, ...evidence]));

    const mapEvidence: FileUpload[] = evidence.map((ev) => {
      return {
        id: ev.id,
        file: ev.file.document,
        date: ev.file.createdAt,
        user: ev.createdBy as User,
        isPublic: ev.isPublic,
      };
    });
    const newEvidenceList = existingFiles.length > 0 ? [...existingFiles, ...mapEvidence] : [...mapEvidence];
    files.forEach(f => {
      if (!f.id || newEvidenceList.find(nel => nel.id === f.id)) return;
      deleteEvidence(f.id);
    });

    setFiles(newEvidenceList);
    setNFiles([]);
    const auditLog = await createNewAuditLog('post', 'Evidence', 'Add New Evidence');
    if (auditLog) {
      dispatch(setAuditLogs([auditLog]));
      newLastUpdated.unshift(auditLog);
      setLastUpdated(newLastUpdated);
    }
    if (questionStatus[question.id].questionAnswer) {
      const attachments = newEvidenceList.map((ev) => ev.id!);
      const updatedQuesitonAnswer = {
        ...questionStatus[question.id].questionAnswer!,
        ...buildAnswerPayload(getValues()),
        attachments,
      };
      updateQuestionAnswer(updatedQuesitonAnswer).then(
        (qaResult: QuestionAnswer) => {
          setCurQuestionAnswer(qaResult);
          handleUpdateState(qaResult);
        },
      );
    } else {
      createQuestionAnswer({
        question: question.id!,
        attachments: newEvidenceList.map((ev) => ev.id!),
        questionnaire: questionnaire!.id!,
        status: 'incomplete',
        survey: survey.id,
        ...buildAnswerPayload(getValues()),
      }).then((qaResult: QuestionAnswer) => {
        setCurQuestionAnswer(qaResult);
        createNewAuditLog('post', 'Answer', 'Create Question Answer', qaResult).then((log) => {
          if (!log) return;
          dispatch(setAuditLogs([log]));
          newLastUpdated.unshift(log);
          setLastUpdated(newLastUpdated);
        });
        handleUpdateState(qaResult);
      });
    }
  };

  const postComment = useCallback(async (text: string, attachments: number[]) => {
    if (!assessment || !user || !assessmentQuestionsStatus) return;
    const comment = await createComment({ user, text, attachments });
    const auditLog = await createNewAuditLog('post', 'Comment', 'Add New Comment');
    try {
      await handleUpdateAssessmentQuestion({ comment, auditLog, retainAssessorStatus: true, hideAlert: true });
    } catch (error) {
      setErrorMessage('An error occurred while posting your comment. Please try again.');
    }
  }, [assessment, assessmentQuestionsStatus, subRemediations]);

  const companyCallback = (nCompany: Company) => {
    if (!survey || !nCompany.companyCertifications) return;
    const approvedCerts = nCompany.companyCertifications.filter((cert) => cert.adminApproved && (!blueBadge || moment(cert.createdAt).unix() < moment(blueBadge.createdAt).unix()));
    let newCompanyCerts = !survey.application && !survey.site ? approvedCerts : [];
    if (survey.application && survey.version && applicationVersions.length && nCompany.companyApplications) {
      const companyApplication = nCompany.companyApplications.find((CA) => CA.application.id === survey.application);
      if (!companyApplication) return;
      approvedCerts.forEach((ccert) => {
        if (
          ccert.applicationVersions?.find((c) =>
            applicationVersions.find((a) => a.id === c && (a.version as Version).value === survey.version),
          )
        )
          newCompanyCerts.push(ccert);
      });
    }
    if (survey.site) {
      approvedCerts.forEach((ccert) => {
        if (ccert.sites.some((app) => app.id === survey.site)) newCompanyCerts.push(ccert);
      });
    }
    dispatch(setCompanyCertifications(newCompanyCerts));
    handleUploadDocumentClose();
    handleCertUploadedOpen();
  };

  const checkOffQuestionnaireLogic = () => {
    if (
      !questionStatus ||
      !questions ||
      Object.keys(questionStatus).length === 0 ||
      !question ||
      !question.id ||
      !questionStatus[question.id] ||
      question.inclusions.length === 0
    )
      return false;
    let offQuestionnaireLogic = 0;
    question.inclusions.forEach((inc) => {
      const fquestion = questions.filter((q) => q.id === (inc.question as Question).id);
      if (fquestion.length === 0) offQuestionnaireLogic++;
    });
    return offQuestionnaireLogic === question.inclusions.length;
  };

  useEffect(() => {
    setSubRemErr('');
  }, [stepIndex]);

  useEffect(() => {
    if (Object.values(subRemNVFiles).every((subRemFile) => subRemFile.length === 0)) {
      setSubRemErr('');
    }
  }, [subRemNVFiles]);

  const handleUpdateRemediation = async () => {
    if (assessmentQuestionsStatus && ([PARTIALLY_IMPLEMENTED, NOT_IMPLEMENTED].includes(assessmentQuestionsStatus[question.id].answer || '')) && subRemediations.length > 0) {
      // Updating sub remediation plan
      setSubRemErr('');
      if (!assessmentQuestionsStatus || !user) return;

      if (Object.values(subRemNVFiles).some((subRemFile) => subRemFile.some((file) => file.isPublic === ''))) {
        setSubRemErr('All Evidence must have a CO Visibility value');
        return;
      }

      try {
        setShowSavingCircle(true);
        const auditLog = await createNewAuditLog('patch', 'Remediate', 'Add/Update Remediation');
        // Process files and generate evidence map
        const subRemEvidence = await processFiles(subRemNVFiles, user);
        if (Object.values(subRemEvidence).includes(null)) {
          setSubRemErr('Failed to upload your file');
          return;
        }
        dispatch(setEvidence(Object.values(subRemEvidence).flat() as Evidence[]));

        const evidenceMap: { [key: string]: FileUpload[] } = Object.entries(subRemEvidence).reduce((acc, [key, value]) => {
          if (!value) return acc;
          acc[key] = value.map(ev => ({
            id: ev.id,
            file: ev.file.document,
            date: ev.file.createdAt,
            user: ev.createdBy as User,
            isPublic: ev.isPublic,
          }));
          return acc;
        }, {} as { [key: string]: FileUpload[] });

        setSubRemVFiles(evidenceMap);
        setSubRemNVFiles({});
        const existingAttachments = subRemediations.reduce((acc, { attachments }) => {
          if (!attachments) return acc;
          return [...acc, ...(attachments as number[])];
        }, [] as number[]);

        const uniqueAttachmentIds = [
          ...new Set([
            ...Object.values(evidenceMap).flat().map((ev) => ev.id),
            ...existingAttachments,
          ]),
        ] as number[];

        const sanitizedSubRemediations = sanitizeRemediationQuestionAnswers(
          evidenceMap,
          question.id,
          subRemediations,
          assessmentQuestionsStatus,
        );

        await handleUpdateAssessmentQuestion({
          auditLog,
          attachments : uniqueAttachmentIds,
          subRemediations: sanitizedSubRemediations,
          savedSubRemFiles: evidenceMap,
        });

        // If any sub remediation is marked as remediated, create one adminOnly notification
        if (needsVerification && subRemediations.some(subRem => subRem.remediationPlan === REMEDIATION_OPTIONS.remediated) && assessmentQuestionsStatus[question.id]?.vendorStatus !== REMEDIATION_OPTIONS.remediated && assessment?.id && workflow?.assessmentSurvey) {
          createAssessmentNotification(assessment.id, workflow.assessmentSurvey, question.id, ADMIN_REMEDIATION_VALIDATION, NOTIFICATION_TYPES.admin_remediation_validation, true);
        }
        dispatch(setShowRemediationSaveAlert(true));
      } catch (e) {
        console.error(e);
        setErrorMessage('Error updating remediation');
      } finally {
        setShowSavingCircle(false);
      }
    } else {
      if (nvfiles.some((file) => file.isPublic === '')) {
        setErrorMessage('All Evidence must have a CO Visibility value');
        return;
      }

      if (!remediationDate || !moment(remediationDate).isValid()) {
        setRemediationDateErr('Remediation Date is required');
        return;
      }

      if (!vendorStatus) {
        setErrorMessage('Select a Remediation Plan');
        return;
      }
      if (
        vendorStatus &&
        (!remediationComment || remediationComment.length === 0)
      ) {
        setErrorMessage('Additional Details is required');
        return;
      }

      // Error if plan is Will not Remediate and no reason is provided
      if (vendorStatus && vendorStatus === REMEDIATION_OPTIONS.willNotRemediate && !willNotRemediateReason) {
        setErrorMessage('Will not Remediate requires a Reason');
        return;
      }

      if (vendorStatus && vendorStatus === REMEDIATION_OPTIONS.willRemediateLater && !remediationDate) {
        setErrorMessage('Will Remediate Later requires a Remediation Date');
        return;
      }

      setErrorMessage('');

      if (!assessmentQuestionsStatus || !user) return;

      try {
        const auditLog = await createNewAuditLog('patch', 'Remediate', 'Add/Update Remediation');

        const onlyNewFiles = nvfiles.filter((file) => typeof file.file !== 'string');
        const existingFiles = nvfiles.filter((file) => typeof file.file === 'string');
        const evidence = await uploadMultipleAttachments(
          onlyNewFiles,
          user,
        );

        dispatch(setEvidence(evidence));
        const mapEvidence: FileUpload[] = evidence.map((ev) => {
          return {
            id: ev.id,
            file: ev.file.document,
            date: ev.file.createdAt,
            user: ev.createdBy as User,
            isPublic: ev.isPublic,
          };
        });
        const newEvidenceList = existingFiles.length > 0 ? [...existingFiles, ...mapEvidence] : [...mapEvidence];
        setvFiles(newEvidenceList);
        setNvFiles([]);
        const attachments = newEvidenceList.map((ev) => ev.id as number);

        await handleUpdateAssessmentQuestion({ auditLog, attachments });
        const nextAssessmentQuestion = Object.values(assessmentQuestionsStatus)
          .sort((a, b) => (a.question as number) - (b.question as number))
          .find((value) => {
            return (value.status === 'remediation' && value.vendorStatus !== REMEDIATION_OPTIONS.remediated && value.question > question.id);
          });

        if (nextAssessmentQuestion) {
          const nextRemQuestion = await getQuestion(nextAssessmentQuestion?.question as number);
          dispatch(setNextRemediationQuestion(nextRemQuestion));
        }
        // Create adminOnly notification if status is Remediated and prev status was not
        if (needsVerification && vendorStatus === REMEDIATION_OPTIONS.remediated && assessmentQuestionsStatus[question.id]?.vendorStatus !== REMEDIATION_OPTIONS.remediated && assessment?.id && workflow?.assessmentSurvey) {
          createAssessmentNotification(assessment.id, workflow.assessmentSurvey, question.id, ADMIN_REMEDIATION_VALIDATION, NOTIFICATION_TYPES.admin_remediation_validation, true);
        }
        dispatch(setShowRemediationSaveAlert(true));
        handleRemediationModalClose();
      } catch (e) {
        console.error(e);
        setErrorMessage('Failed to upload your file');
        return;
      }
    }
  };

  const setContentOwnerPriority = async () => {
    if (
      !curQuestionStatus ||
      !assessmentQuestionsStatus ||
      !assessmentQuestionsStatus[question.id] ||
      !user ||
      user.type !== USER_TYPES.CONTENT_OWNER
    )
      return;

    const assessmentAnswer = assessmentQuestionsStatus[question.id];
    const companyId = typeof user.companies[0] === 'number' ? user.companies[0] : user.companies[0].id;
    if (!companyId) return;
    const coids: number[] = assessmentAnswer.contentOwnerPriority
      ? assessmentAnswer.contentOwnerPriority.map((co) => (typeof co === 'number' ? co : co.id ? co.id : -1))
      : [];
    const contentOwnerPriority =
      coids.indexOf(companyId) === -1 ? coids.concat(companyId) : coids.filter((id) => id !== companyId);

    const auditLog =
      coids.indexOf(companyId) === -1
        ? await createNewAuditLog('post', 'Content Owner Prioirty', 'Set Content Owner Priority')
        : await createNewAuditLog('delete', 'Content Owner Prioirty', 'Remove Content Owner Priority');

    try {
      await handleUpdateAssessmentQuestion({
        auditLog,
        contentOwnerPriority: contentOwnerPriority ? contentOwnerPriority : [],
      });
    } catch (error) {
      setErrorMessage('An error occurred while updating content owner priority. Please try again.');
    }
  };

  const filteredComments = comments?.filter((comment) => {
    const assessmentSurvey = assessment
      ? (assessment.surveys as AssessmentSurvey[]).find((asssur) =>
        typeof asssur.survey === 'number' ? asssur.survey === survey?.id : asssur.survey.id === survey?.id,
      )
      : undefined;

    return (
      user?.type !== USER_TYPES.CONTENT_OWNER ||
      (assessmentSurvey &&
        assessmentSurvey.reportIssueDate &&
        comment.createdAt &&
        assessmentSurvey.reportIssueDate < comment.createdAt)
    );
  });

  const filteredFiles = () => {
    if (user?.type === USER_TYPES.CONTENT_OWNER) {
      // if already an Assessment OR status is submittedForAssessment
      if (survey?.status === 'submittedForAssessment' || assessment) {
        return files.filter((file) => file.isPublic);
      }
      return [];
    }

    return files;
  };

  const handleSetShowPIDetails = useCallback((e: React.MouseEvent | undefined) => {
    if (e) e.preventDefault();
    if (!question.id) return;

    const { assessorQuestionAnswers = [], answer = '' } = assessmentQuestionsStatus && assessmentQuestionsStatus[question.id] ? assessmentQuestionsStatus[question.id] : {};
    if ([PARTIALLY_IMPLEMENTED, NOT_IMPLEMENTED].includes(answer)) {
      if (assessorQuestionAnswers && assessorQuestionAnswers.length > 0) {
        setSelectedCtrlAns(assessorQuestionAnswers.reduce((acc: { [key: string]: PIDetails }, curr) => {
          acc[curr.answer as number] = {
            compensatingOrNotApplicable: baseSelectedCtrlAns[curr.answer as number]?.compensatingOrNotApplicable || curr.compensatingOrNotApplicable,
            isImplemented: baseSelectedCtrlAns[curr.answer as number]?.isImplemented || curr.isImplemented,
            comment: baseSelectedCtrlAns[curr.answer as number]?.comment || curr.comment,
            id: curr.id,
            answer: curr.answer,
          };
          return acc;
        }, {}));
      } else {
        // Prime each answer with a default value
        primeSelectedCtrlAns();
      }
    } else if ([PARTIALLY_IMPLEMENTED, NOT_IMPLEMENTED].includes(assessorAnswer) && workflow?.status === 'Assessing') {
      // Do not prime if baseSelectedCtrlAns is already in default state
      const hasAnyValidValue = Object.values(baseSelectedCtrlAns).some((value) => value.isImplemented !== false || value.compensatingOrNotApplicable !== '' || value.comment !== '');
      if (hasAnyValidValue) {
        setShowPIDetails(true);
        return;
      }
      primeSelectedCtrlAns();
    }
    if (assessorAnswer === PARTIALLY_IMPLEMENTED) setShowPIDetails(true);
  }, [setShowPIDetails, assessmentQuestionsStatus, question.id, assessorAnswer, workflow?.status, baseSelectedCtrlAns]);

  useEffect(() => {
    if (showPIDetails) {
      handleSetShowPIDetails(undefined);
    }
  }, [showPIDetails]);

  const handleSetAssessorAnswer = useCallback((answer: string) => {
    setShowPIDetails([PARTIALLY_IMPLEMENTED].includes(answer) && isFiveThreeOrGreater);
    setAssessorAnswer(answer);
  }, [setAssessorAnswer]);

  useEffect(() => {
    if (!assessmentQuestionsStatus || !question.id || !assessmentQuestionsStatus[question.id]) return;

    const { assessorQuestionAnswers, remediationQuestionAnswers } = assessmentQuestionsStatus[question.id];

    if (assessorQuestionAnswers && assessorQuestionAnswers.length > 0) {
      setSelectedCtrlAns([...assessorQuestionAnswers].reduce((acc: { [key: string]: PIDetails }, curr) => {
        acc[curr.answer as number] = {
          id: curr.id,
          compensatingOrNotApplicable: curr.compensatingOrNotApplicable,
          isImplemented: curr.isImplemented,
          comment: curr.comment,
          answer: curr.answer,
        };
        return acc;
      }, {}));
    }

    if (remediationQuestionAnswers && remediationQuestionAnswers.length > 0) {
      const existingSubRemediations = [...remediationQuestionAnswers].map((remediation) => ({
        id: remediation.id,
        assessorAnswer: remediation.assessorAnswer,
        remediationPlan: remediation.remediationPlan,
        willNotRemediateReason: remediation.willNotRemediateReason,
        remediationComment: remediation.remediationComment,
        remediationDate: remediation.remediationDate,
        attachments: remediation.attachments,
        answer: remediation.answer,
        text: question.answers.find((answer) => answer.id === remediation.answer)?.text,
      }));

      existingSubRemediations.sort((a, b) => {
        const aId = typeof a.answer === 'number' ? a.answer : (a.answer as Question).id;
        const bId = typeof b.answer === 'number' ? b.answer : (b.answer as Question).id;
        return aId - bId;
      });

      const dedupedExisting = existingSubRemediations.reduce((acc: typeof existingSubRemediations, curr) => {
        const answerId = typeof curr.answer === 'number' ? curr.answer : (curr.answer as Question)?.id;
        const existingIndex = acc.findIndex((s) => {
          const sid = typeof s.answer === 'number' ? s.answer : (s.answer as Question)?.id;
          return sid === answerId;
        });
        if (existingIndex === -1) {
          acc.push(curr);
        } else if (curr.id && !acc[existingIndex].id) {
          acc[existingIndex] = curr;
        }
        return acc;
      }, []);

      setSubRemediations(dedupedExisting);
    } else {
      setSubRemediationFromAssessorQuestionAnswers();
    }
  }, [assessmentQuestionsStatus, question.id]);

  useEffect(() => {
    // On change of subRemediations, set parent data if all sub-remediations are valid
    if (!subRemediations || subRemediations.length === 0 || !subRemediations.every(subRem => subRem.remediationPlan &&
        (subRem.willNotRemediateReason || [REMEDIATION_OPTIONS.remediated, REMEDIATION_OPTIONS.willRemediateLater].includes(subRem.remediationPlan)) &&
        subRem.remediationDate && subRem.remediationComment?.trim())) return;
    const parentVendorStatus = subRemediations.reduce((acc: string, curr) => {
      if (acc === REMEDIATION_OPTIONS.willNotRemediate) return acc;
      if (curr.remediationPlan === REMEDIATION_OPTIONS.willNotRemediate) {
        return REMEDIATION_OPTIONS.willNotRemediate;
      } else if (curr.remediationPlan === REMEDIATION_OPTIONS.willRemediateLater) {
        return REMEDIATION_OPTIONS.willRemediateLater;
      }
      return acc;
    }, REMEDIATION_OPTIONS.remediated);

    setVendorStatus(parentVendorStatus);
    setRemediationDate(remediationDate || new Date().toISOString());
    setRemediationComment('');
  }, [subRemediations]);


  useEffect(() => {
    setBaseSelectedCtrlAns({ ...JSON.parse(JSON.stringify(selectedCtrlAns)) });
  }, [selectedCtrlAns]);

  useEffect(() => {
    // If the assessorAnswer is not Partially Implemented and not Not Implemented, clear the selectedCtrlAns all values are default
    if (![PARTIALLY_IMPLEMENTED, NOT_IMPLEMENTED].includes(assessorAnswer) && isFiveThreeOrGreater) {
      const hasDefaulted = Object.values(selectedCtrlAns).every((value) => value.isImplemented === false && value.compensatingOrNotApplicable === '' && value.comment === '');
      if (hasDefaulted) {
        setBaseSelectedCtrlAns({});
      }
    }
  }, [assessorAnswer, isFiveThreeOrGreater]);

  const handleStep = useCallback((step: number) => {
    setStepIndex(step);
  }, [setStepIndex]);

  const [originalVerificationStatus, setOriginalVerificationStatus] = useState<string | undefined>(assessmentQuestionsStatus && assessmentQuestionsStatus[question.id]?.verificationStatus);
  const [originalVerificationComment, setOriginalVerificationComment] = useState<string | undefined>(assessmentQuestionsStatus && assessmentQuestionsStatus[question.id]?.verificationComment);
  const [originalVerificationDate, setOriginalVerificationDate] = useState<string | undefined>(assessmentQuestionsStatus && assessmentQuestionsStatus[question.id]?.verificationDate);
  const [origSubRemediation, setOrigSubRemediation] = useState<SubRemediation | undefined>(undefined);

  useEffect(() => {
    const qStatus = assessmentQuestionsStatus?.[question.id];
    if (!qStatus || !Array.isArray(qStatus.remediationQuestionAnswers)) {
      setOrigSubRemediation(undefined);
      return;
    }
    const sortedAnswers = qStatus.remediationQuestionAnswers
      ?.slice()
      .sort((a, b) => {
        const answerA =
          typeof a?.answer === 'number'
            ? (a.answer as number)
            : !!a.answer
              ? (a.answer as Question).id
              : 0;

        const answerB =
          typeof b?.answer === 'number'
            ? (b.answer as number)
            : !!b.answer
              ? (b.answer as Question).id
              : 0;

        return answerA - answerB;
      },
      );
    setOrigSubRemediation(sortedAnswers?.[stepIndex] ?? undefined);
  }, [assessmentQuestionsStatus, question.id, stepIndex, remediationModal]);

  const processVerificationBadge = useCallback( async (newAssessmentQuestionsStatus: AssessmentQuestionStatusById) => {
    const verificationStatusCheck = determineVerificationStatus(newAssessmentQuestionsStatus, questionStatus);

    if (!verificationStatusCheck || !workflow?.badges?.length) return;

    let updatedWorkflow: Workflow = { ...workflow, badges: [...workflow.badges] as (Badge)[] };
    let badgeTypeAwarded: typeof GOLD_NEXT_GEN_BADGE_LABEL | typeof GOLD_STAR_BADGE_LABEL | undefined;

    const { qualifyingGold, qualifyingGoldStar } = verificationStatusCheck;

    if (qualifyingGold && !updatedWorkflow.badges!.some((badge) => typeof badge !== 'number' && badge.title === GOLD_NEXT_GEN_BADGE_LABEL)) {
      const nextGenEligible = await eligibleForNextGenGoldBadge(updatedWorkflow.id);
      if (nextGenEligible) {
        const newBadge = await processVerificationAward(GOLD_NEXT_GEN_BADGE_LABEL, updatedWorkflow.id);
        if (newBadge) {
          updatedWorkflow.badges = [...updatedWorkflow.badges!, newBadge] as Badge[];
          await dispatch(updateWorkflow({ id: updatedWorkflow.id, badges: [...updatedWorkflow.badges, newBadge] }));
        }
        badgeTypeAwarded = GOLD_NEXT_GEN_BADGE_LABEL;
      }
    }

    if (qualifyingGoldStar && !updatedWorkflow.badges!.some((badge) => typeof badge !== 'number' && badge.title === GOLD_STAR_BADGE_LABEL)) {
      const goldStarEligible = await eligibleForGoldStarBadge(updatedWorkflow.id);
      if (goldStarEligible) {
        const newBadge =  await processVerificationAward(GOLD_STAR_BADGE_LABEL, updatedWorkflow.id);
        updatedWorkflow.badges = [...updatedWorkflow.badges!, newBadge] as Badge[];
        if (newBadge) {
          await dispatch(updateWorkflow({ id: updatedWorkflow.id, badges: [...updatedWorkflow.badges, newBadge] }));
        }
        badgeTypeAwarded = GOLD_STAR_BADGE_LABEL;
      }
    }

    if (badgeTypeAwarded) {
      setVerificationAwardModalType(badgeTypeAwarded);
    }
  }, [workflow, questionStatus]);

  const handleRemediationValidationStatusChange = useCallback( async (verificationStatus: string, verificationComment: string) => {
    if (!assessmentQuestionsStatus) return;
    const newAnswer = {
      ...assessmentQuestionsStatus[question.id],
      verificationStatus,
      verificationComment,
      verificationDate: new Date().toISOString(),
    };
    await updateAssessorAnswer(newAnswer);
    dispatch(setShowRemediationValidationAlert(true));

    setOriginalVerificationStatus(verificationStatus);
    setOriginalVerificationComment(verificationComment);
    setOriginalVerificationDate(newAnswer.verificationDate);
    if (!assessment?.id) return;

    // Create notification if verification status is changed and not in notStarted status
    if (originalVerificationStatus !== verificationStatus && verificationStatus !== REMEDIATION_VERIFICATION_STATUS.notStarted && workflow?.assessmentSurvey) {
      await createAssessmentNotification(assessment?.id, workflow.assessmentSurvey, question.id, REMEDIATION_VALIDATION_STATUS_UPDATE, `Validation status changed to ${verificationStatus}`, false);
    }
    dispatch(storeFetchAssessment(assessment?.id));
  }, [assessmentQuestionsStatus, question.id, assessment, workflow, updateAssessorAnswer, originalVerificationStatus]);

  const handleSetShowAutoAnsweredByCertModalClose = useCallback(() => {
    dispatch(setAnsweredByCertModalData(null));
  }, [dispatch]);

  const handleSubRemediationValidationStatusChange = useCallback( async (verificationStatus: string, verificationComment: string) => {
    if (!assessmentQuestionsStatus) return;

    const subRemediation = subRemediations[stepIndex];
    if (!subRemediation) return;
    const statusChanged = !origSubRemediation || origSubRemediation.verificationStatus !== verificationStatus;
    const commentChanged = (origSubRemediation?.verificationComment || '') !== verificationComment;
    if (!statusChanged && !commentChanged) return;
    subRemediation.verificationStatus = verificationStatus;
    subRemediation.verificationDate = new Date().toISOString();
    subRemediation.verificationComment = verificationComment;
    subRemediation.attachments = subRemediation.attachments?.map((att) => (typeof att === 'number' ? att : att.id)) as number[];
    subRemediation.assessorAnswer = typeof subRemediation.assessorAnswer === 'number' ? subRemediation.assessorAnswer : (subRemediation.assessorAnswer as AssessorAnswer).id as number;
    subRemediation.answer = (subRemediation.answer as Question).id;  

    setOrigSubRemediation((prev) => {
      if (!prev) return prev;
      return { ...prev, verificationStatus, verificationDate: new Date().toISOString(), verificationComment };
    });

    await handleSaveSubRemediations([subRemediation]);
    dispatch(setShowRemediationValidationAlert(true));
    if (!assessment?.id) return;
    const newAnswer = {
      ...assessmentQuestionsStatus[question.id],
      remediationQuestionAnswers: [...(assessmentQuestionsStatus[question.id].remediationQuestionAnswers || [])]
        .sort((a, b) => {
          const answerA = typeof a.answer === 'number' ? a.answer : !!a.answer ? a.answer.id : 0;
          const answerB = typeof b.answer === 'number' ? b.answer : !!b.answer ? b.answer.id : 0;
          return answerA - answerB;
        })
        .map((remediation, index) => {
          return index === stepIndex
            ? {
              ...remediation,
              verificationStatus,
              verificationComment,
              verificationDate: new Date().toISOString(),
            }
            : remediation;
        }),
    };

    // Update assessmentQuestionStatus with new validation status
    const newAssessmentQuestionsStatus = { ...assessmentQuestionsStatus, [question.id]: newAnswer };
    await dispatch(setAllAssessmentQuestionStatus(newAssessmentQuestionsStatus));
    await updateAssessorAnswer(newAnswer);
    if (statusChanged && verificationStatus !== REMEDIATION_VERIFICATION_STATUS.notStarted && workflow?.assessmentSurvey) {
      await createAssessmentNotification(assessment?.id, workflow.assessmentSurvey, question.id, REMEDIATION_VALIDATION_STATUS_UPDATE, `Validation status changed to ${verificationStatus}`, false);
    }

    dispatch(storeFetchAssessment(assessment?.id));
  
    if ([TPN_BEST_PRACTICE_5_3, TPN_BEST_PRACTICE_5_1, TPN_BEST_PRACTICE_5_2].includes(workflow?.workflowType as WorkflowTypeKey)) return;

    processVerificationBadge(newAssessmentQuestionsStatus);
  }, [workflow, assessmentQuestionsStatus, question.id, assessment, subRemediations, origSubRemediation, handleSaveSubRemediations, stepIndex]);

  const patchRemediationValidationStatus = useCallback(async (verificationStatus: string, isSubRemediation: boolean, comment: string) => {
    const method = !originalVerificationComment ? 'post' : 'patch';
    const description = !originalVerificationComment
      ? `Status: "${verificationStatus}"\nComment: "${comment}"`
      : `Status changed from "${originalVerificationStatus}" to "${verificationStatus}"\nComment changed from "${originalVerificationComment}" to "${comment}".`;

    try {
      const verificationAuditLog = await createNewAuditLog(method, LABELS.remediationValidation, description);
      await handleUpdateAssessmentQuestion({ auditLog: verificationAuditLog });
    } catch (e) {
      console.error(e);
    } finally {
      if (isSubRemediation) {
        handleSubRemediationValidationStatusChange(verificationStatus, comment);
      } else {
        handleRemediationValidationStatusChange(verificationStatus, comment);
      }
    }
  }, [handleSubRemediationValidationStatusChange, handleRemediationValidationStatusChange, originalVerificationComment, originalVerificationStatus]);

  // Lock when 2+ years have passed since TPN approval date
  const lockRemediation = useMemo(() => {
    const currSurvey = assessment?.surveys?.[0];
    if (!currSurvey || typeof currSurvey === 'number') return false;

    const issueDate = currSurvey.reportIssueDate;
    if (!issueDate) return false;

    const publicationDate = moment(issueDate);

    return moment().isAfter(publicationDate.clone().add(2, 'years'));
  }, [assessment?.surveys]);

  const coPriorityBtnLabel = useMemo(() => {
    const contentOwnerPriority = assessmentQuestionsStatus?.[question.id]?.contentOwnerPriority as number[] | undefined;
    const companyId = user?.companies[0]?.id;

    if (
      curQuestionStatus === 'remediation' ||
      (curQuestionStatus !== 'remediation' &&
      contentOwnerPriority &&
      companyId &&
      !contentOwnerPriority.includes(companyId))
    ) {
      return 'Mark as Priority';
    } else {
      return 'Mark not Priority';
    }
  }, [curQuestionStatus, assessmentQuestionsStatus, user, question.id]);

  const isQuestionAnswerIncomplete: boolean = useMemo(() => questionStatus[question.id].status === 'incomplete', [questionStatus, question.id]);

  // Baseline only logic to determine if the baseline is readonly
  useEffect(() => {
    if (!questionnaire?.type || ![QuestionnaireTypes.APPLICATION_BASELINE, QuestionnaireTypes.SITE_BASELINE].includes(questionnaire.type)) {
      return;
    }

    if (ReadOnlyBaselineWorkflowStatusArray.includes(workflow?.status as WorkflowStatus) || user?.type !== USER_TYPES.VENDOR_ADMIN) {
      dispatch(setReadOnly(true));
    } else if (!isQuestionnaireArchived) {
      dispatch(setReadOnly(false));
    }
  }, [workflow?.status, questionnaire?.type, user?.type, isQuestionnaireArchived]);

  const badgeTitle =
  verificationAwardModalType === GOLD_NEXT_GEN_BADGE_LABEL
    ? 'Gold(NextGen) Badge Awarded'
    : 'Gold Star Badge Awarded';

  return (
    <>
      <AutoAnsweredByCertModal
        isOpen={Boolean(answeredByCertModalData)}
        onClose={handleSetShowAutoAnsweredByCertModalClose}
      />
      <AssessorPIDetails
        updateAssessorFindings={(findings : string) => setAssessorFinding(findings)}
        assessorFinding={assessorFinding}
        question={question}
        control={control}
        canEdit={user?.type === USER_TYPES.ASSESSOR}
        show={showPIDetails}
        setShow={setShowPIDetails}
        selectedCtrlAns={baseSelectedCtrlAns}
        setSelectedCtrlAns={setBaseSelectedCtrlAns} 
      />
      <Snackbar
        open={showRemediationSaveAlert}
        autoHideDuration={4000}
        TransitionComponent={Slide}
        onClose={() => dispatch(setShowRemediationSaveAlert(false))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => dispatch(setShowRemediationSaveAlert(false))} icon={<ThumbUp fontSize="inherit" />} severity="success" sx={{ width: '100%' }}>
        Remediation updated
        </Alert>
      </Snackbar>
      <Snackbar
        open={showAssessorFindingsAlert}
        autoHideDuration={4000}
        TransitionComponent={Slide}
        onClose={() => dispatch(setShowAssessorFindingsAlert(false))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => dispatch(setShowAssessorFindingsAlert(false))} icon={<ThumbUp fontSize="inherit" />} severity="success" sx={{ width: '100%' }}>
        Assessor Finding updated
        </Alert>
      </Snackbar>
      <Snackbar
        open={showRemediationValidationAlert}
        autoHideDuration={4000}
        TransitionComponent={Slide}
        onClose={() => dispatch(setShowRemediationValidationAlert(false))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => dispatch(setShowRemediationValidationAlert(false))} icon={<ThumbUp fontSize="inherit" />} severity="success" sx={{ width: '100%' }}>
        Remediation Validation updated
        </Alert>
      </Snackbar>
      {questionStatus &&
        Object.keys(questionStatus).length > 0 &&
        question &&
        question.id &&
        (Boolean(props.incompleteQuestionIds && props.incompleteQuestionIds[question.id]) ||
          ( props.incompleteQuestionIds === undefined &&
          curQuestionStatus !== 'hide' || (curQuestionStatus === 'hide' && preview && checkOffQuestionnaireLogic())
          )
        ) && (
          <>
            <Card
              sx={{
                mb: 1,
                ml: 0,
                backgroundColor:
                  color === 'info'
                    ? 'white'
                    : getBackgroundColor(theme.palette[color].light, color === 'assessorApproved' ? 0.7 : 0.9),
              }}
            >
              <Grid
                container
                spacing={2}
                sx={{ p: 2 }}
                component='form'
                method='post'
                onSubmit={handleSubmit(submitAnswer)}
              >
                <Grid
                  container
                  item
                  xs={12}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setQuestionHandler();
                  }}
                >
                  <QuestionOnQuestionnaireHeader
                    questions={questions}
                    question={question}
                    comments={comments}
                    inclusion={inclusion}
                    certificationControl={certificationControl}
                    curQuestionStatus={curQuestionStatus}
                    color={color}
                    onCommentModalOpen={handleCommentModalOpen}
                  />
                </Grid>
                {expanded && (
                  <>
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{question.description}</Typography>
                    </Grid>
                    <QuestionOnQuestionnaireAnswer
                      question={question}
                      workflowType={workflow?.workflowType}
                      questionStatus={questionStatus}
                      curQuestionStatus={curQuestionStatus}
                      control={control}
                      getValues={getValues}
                      setValue={setValue}
                      register={register}
                      readOnly={readOnly || !workflow?.isActive}
                      preview={preview}
                      submitted={submitted}
                      color={color}
                    />
                    <AttachmentFormAndComment
                      assessorAnswerSubmitted={!!assessmentQuestionsStatus?.[question.id]}
                      user={user}
                      question={question}
                      comments={filteredComments}
                      assessment={assessment}
                      files={filteredFiles()}
                      register={register}
                      readOnly={readOnly || !workflow?.isActive}
                      preview={preview}
                      submitted={submitted}
                      onAttachmentModalOpen={handleAttachmentModalOpen}
                      onCommentModalOpen={handleCommentModalOpen}
                      onAssessorFindingsModalOpen={handleAssessorFindingModalOpen}
                      surveyComplete={['submittedForAssessment', 'complete'].includes(survey?.status ?? '')}
                    />
                    <Divider sx={{ ml: 2, mt: 2, width: 'calc(100% - 16px)' }} />
                    <Grid
                      item
                      xs={12}
                      sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse' }}
                    >
                      {['remediation', 'priority', 'submitted'].indexOf(curQuestionStatus.toLowerCase()) === -1 &&
                        (!readOnly || (readOnly && preview)) && (
                        <AsyncSaveButton
                          variant="contained"
                          color="primary"
                          type="submit"
                          label="Save and Continue"
                          endIconWhenIdle={<NavigateNext />}
                          disabled={!workflow?.isActive}
                          isSaving={isSavingAnswer}
                        />
                      )}
                      {['priority', 'submitted'].indexOf(curQuestionStatus.toLowerCase()) === -1 &&
                        readOnly && <Box />}
                      {assessmentStatus &&
                        ['Assessing', 'Submitted', 'Remediation'].indexOf(assessmentStatus) !== -1 && (
                          <>
                            {((user && user.type === USER_TYPES.ASSESSOR) || preview) &&
                              ['priority'].indexOf(curQuestionStatus) === -1 && !isAssessmentFindingsOpen &&
                              !isQuestionAnswerIncomplete && (
                                <Button
                                    variant='outlined'
                                    sx={{ backgroundColor: 'white' }}
                                    color='primary'
                                    startIcon={<Check />}
                                    onClick={() => setIsAssessmentFindingsOpen(true)}
                                  >
                                    {assessorAnswer ? 'Update Finding' : 'Assess'}
                                </Button>
                            )
                            }
                            {['remediation', 'priority'].indexOf(curQuestionStatus.toLowerCase()) !== -1 &&
                              assessmentStatus &&
                              user && user.type !== USER_TYPES.ASSESSOR &&
                              assessmentStatus !== 'Remediation' && !isAssessmentFindingsOpen && (
                                <>
                                  <Box>
                                    {((user && user.type === USER_TYPES.ASSESSOR) || preview) && (
                                      <Button
                                        variant='outlined'
                                        sx={{ backgroundColor: 'white', mr: 1 }}
                                        color='error'
                                        startIcon={<Warning />}
                                        onClick={() => setIsAssessmentFindingsOpen(true)}
                                      >
                                        Update Remediation
                                      </Button>
                                    )}
                                  </Box>
                                </>
                            )}
                          </>
                      )}
                      {assessmentStatus &&
                        ['Remediation', 'Complete', 'Completed'].indexOf(assessmentStatus) > -1 &&
                        ['remediation', 'priority', 'submitted'].indexOf(curQuestionStatus.toLowerCase()) > -1 && (
                          <Box>
                            {((user && user.type === USER_TYPES.CONTENT_OWNER && (assessmentStatus === 'Remediation' || isValidCORemPlan)) ||
                              preview) && (
                              <Button
                                variant='outlined'
                                sx={{ backgroundColor: 'white' }}
                                color='secondary'
                                startIcon={<Check />}
                                onClick={() => setContentOwnerPriority()}
                              >
                                {coPriorityBtnLabel}
                              </Button>
                            )}
                            <Button
                              sx={{ ml: 1 }}
                              variant='contained'
                              startIcon={<Check />}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemediationModalOpen();
                              }}
                            >
                              {user?.type === USER_TYPES.VENDOR_ADMIN
                                ? vendorStatus
                                  ? 'Update Remediation'
                                  : 'Remediate'
                                : 'View Remediation'}
                            </Button>
                          </Box>
                      )}
                    </Grid>
                    {assessmentStatus && (user && user.type === USER_TYPES.ASSESSOR) && (
                      <Collapse in={isAssessmentFindingsOpen} sx={{ width: '100%', px: 3, py: 1 }}>
                        <Typography variant='h5' fontWeight='bold'>Assessor Finding for {question.title}</Typography>
                        <AssessorFindingsForm
                          workflowType={workflow?.workflowType}
                          handleSetShowPIDetails={handleSetShowPIDetails}
                          user={user}
                          question={question}
                          assessorAnswer={assessorAnswer}
                          assessmentStatus={assessmentStatus}
                          assessorFindings={assessorFinding}
                          onAssessorAnswerChange={(answer) => handleSetAssessorAnswer(answer)}
                          onAssessorFindingsChange={(findings) => setAssessorFinding(findings)}
                        />
                        {errorMessage.length > 0 && (
                          <Typography color='error' sx={{ mt: 2 }}>
                            {errorMessage}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', columnGap: 2, mt: 2 }}>
                          <Button
                            variant={
                              user &&
                              user.type === USER_TYPES.ASSESSOR &&
                              assessmentStatus &&
                              ['Assessing', 'Submitted'].indexOf(assessmentStatus) > -1
                                ? 'outlined'
                                : 'contained'
                            }
                            onClick={() => handleAssessorFindingsClose(true)}
                          >
                            {user &&
                            user.type === USER_TYPES.ASSESSOR &&
                            assessmentStatus &&
                            ['Assessing', 'Submitted'].indexOf(assessmentStatus) > -1
                              ? 'Cancel'
                              : 'Close'}
                          </Button>
                          {user &&
                            user.type === USER_TYPES.ASSESSOR &&
                            assessmentStatus &&
                            ['Assessing', 'Submitted'].includes(assessmentStatus) && (
                            <AsyncSaveButton
                              variant="contained"
                              color="primary"
                              onClick={() => handleSaveFindingsClick(true)}
                              isSaving={isSavingFindings}
                              savingLabel="Saving..."
                              label={'Save Findings'}
                              disabled={isSavingFindings}
                            />
                          )}
                        </Box>
                      </Collapse>
                    )}
                    {lastUpdated &&
                      lastUpdated !== undefined &&
                      lastUpdated.length > 0 &&
                      lastUpdated[0] !== undefined && (
                        <Grid item xs={12} sx={{ textAlign: 'right' }} mt={-1.5}>
                          <Typography
                            sx={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAuditLogModalOpen();
                            }}
                            variant='caption'
                          >
                            Last Updated By {(lastUpdated[0].user as User).firstName}{' '}
                            {(lastUpdated[0].user as User).lastName}{' '}
                            {moment(lastUpdated[0].createdAt).format('MM/DD/YYYY HH:mm')}
                          </Typography>
                        </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Card>
            <StandardDialog
              title={`Comments for Question: ${question.title}`}
              handleClose={handleCommentModalClose}
              isOpen={commentModal}
            >
              <Comments
                disabled={user?.type !== USER_TYPES.TPN_ADMIN && (originalVerificationStatus === REMEDIATION_VERIFICATION_STATUS.reviewed)}
                comments={filteredComments}
                handleClose={handleCommentModalClose}
                postComment={postComment}
                assessmentSurvey={
                  assessment
                    ? (assessment.surveys as AssessmentSurvey[]).find((asssur) =>
                      typeof asssur.survey === 'number'
                        ? asssur.survey === survey?.id
                        : asssur.survey.id === survey?.id,
                    )
                    : undefined
                }
              />
            </StandardDialog>
            <StandardDialog
              title={`Audit Logs for Question: ${question.title}`}
              handleClose={handleAuditLogModalClose}
              isOpen={auditLogModal}
              maxWidth='lg'
            >
              <AuditLogModal auditLogs={lastUpdated} handleClose={handleAuditLogModalClose} />
            </StandardDialog>
            <StandardDialog
              title={
                bestPractice && topic && domain
                  ? `${buildBestPracticeTitle({ ...bestPractice, domain, topic })} Complete`
                  : 'Best Practice Complete'
              }
              handleClose={handleBPCompleteClose}
              isOpen={bpComplete}
            >
              <Typography>
                You have completed{' '}
                {bestPractice && topic && domain
                  ? `Best Practice ${buildBestPracticeTitle({ ...bestPractice, domain, topic })}`
                  : 'this Best Practice'}
                , would you like to move on to the next one?
              </Typography>
              <StandardDialogActions>
                <Button variant='outlined' onClick={() => handleBPCompleteClose()}>
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => {
                    debounceNextQuestion();
                    handleBPCompleteClose();
                  }}
                >
                  Continue
                </Button>
              </StandardDialogActions>
            </StandardDialog>
            <StandardDialog
              title={`Upload attachments to question: ${question.title}`}
              handleClose={handleAttachmentModalClose}
              isOpen={attachmentModal}
            >
              {question.attachmentFormLabel && <Typography mb={1}>{question.attachmentFormLabel}</Typography>}
              <FileUploaderComp
                disabled={!workflow?.isActive || readOnly}
                showCOPublicModal={true}
                assessorVisible={true}
                uploadType={'evidence'}
                fileList={filteredFiles()}
                callback={handleSetNFiles}
                dontShowUpload={
                  !user || user.type !== USER_TYPES.VENDOR_ADMIN || (!!assessmentStatus && assessmentStatus !== 'Pre-Assessment')
                }
                enablePublic={true}
              />
              <StandardDialogActions>
                <Button variant='outlined' onClick={() => handleAttachmentModalClose()}>
                  Close
                </Button>
                {user && user.type === USER_TYPES.VENDOR_ADMIN && (!assessmentStatus || assessmentStatus === 'Pre-Assessment') && (
                  <Button variant='contained' onClick={() => uploadAttachments()} disabled={!workflow?.isActive || readOnly}>
                    Save
                  </Button>
                )}
              </StandardDialogActions>
            </StandardDialog>
            <StandardDialog
              maxWidth='xl'
              title={`Assessor Finding for ${question.title}`}
              handleClose={() => handleAssessorFindingModalClose(true)}
              isOpen={assessorFindingModal}
            >
              <AssessorFindingsForm
                workflowType={workflow?.workflowType}
                handleSetShowPIDetails={handleSetShowPIDetails}
                user={user}
                question={question}
                assessorAnswer={assessorAnswer}
                assessmentStatus={assessmentStatus}
                assessorFindings={assessorFinding}
                onAssessorAnswerChange={(answer) => setAssessorAnswer(answer)}
                onAssessorFindingsChange={(findings) => setAssessorFinding(findings)}
              />
              {errorMessage.length > 0 && (
                <Typography color='error' sx={{ mt: 2 }}>
                  {errorMessage}
                </Typography>
              )}
              <StandardDialogActions>
                <Box display="flex" justifyContent="space-between" width="100%">
                <Button
                  variant={
                    user &&
                    user.type === USER_TYPES.ASSESSOR &&
                    assessmentStatus &&
                    ['Assessing', 'Submitted'].indexOf(assessmentStatus) > -1
                      ? 'outlined'
                      : 'contained'
                  }
                  onClick={() => handleAssessorFindingModalClose(true)}
                >
                  {user &&
                  user.type === USER_TYPES.ASSESSOR &&
                  assessmentStatus &&
                  ['Assessing', 'Submitted'].indexOf(assessmentStatus) > -1
                    ? 'Cancel'
                    : 'Close'}
                </Button>
                {user &&
                user.type === USER_TYPES.ASSESSOR &&
                assessmentStatus &&
                ['Assessing', 'Submitted'].includes(assessmentStatus) && (
                <AsyncSaveButton
                  variant="contained"
                  color="primary"
                  onClick={() => handleSaveFindingsClick(false)}
                  isSaving={isSavingFindings}
                  savingLabel='Saving...'
                  label={'Save Findings'}
                  disabled={isSavingFindings}
                  endIconWhenIdle={<NavigateNextIcon />}
                />
                )}
                </Box>
              </StandardDialogActions>
            </StandardDialog>
            <StandardDialog
              title={`Remediate ${question.title}`}
              handleClose={handleRemediationModalClose}
              isOpen={remediationModal}
              maxWidth='lg'
            >
              { lockRemediation && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Remediations cannot be updated after the assessment expiration date.
                </Alert>
              )}
              <RemediationForm
                needsVerification={needsVerification}
                patchRemediationValidationStatus={patchRemediationValidationStatus}
                subRemediations={subRemediations}
                lockRemediation={lockRemediation}
                stepIndex={stepIndex}
                handleStep={handleStep}
                subRemErr={subRemErr}
                setSubRemediations={setSubRemediations}
                handleRemediationModalClose={handleRemediationModalClose}
                assessorAnswer={assessorAnswer}
                setRemediationDateErr={setRemediationDateErr}
                remediationDateErr={remediationDateErr}
                user={user}
                question={question}
                origSubRemediation={origSubRemediation}
                origRemediationDate={assessmentQuestionsStatus && assessmentQuestionsStatus[question.id]?.remediationDate}
                origRemediationStatus={assessmentQuestionsStatus && assessmentQuestionsStatus[question.id]?.vendorStatus}
                origVerificationDate={originalVerificationDate}
                origVerificationStatus={originalVerificationStatus}
                origVerificationComment={originalVerificationComment}
                remediationComment={remediationComment}
                remediationDate={remediationDate}
                vendorStatus={vendorStatus}
                onSubRemVFilesChange={handleSetSubRemNVFile}
                willNotRemediateReason={willNotRemediateReason}
                vfiles={vfiles}
                subRemVFiles={subRemVFiles}
                errorMessage={errorMessage}
                onRemediationCommentChange={(newRemediationComment) => setRemediationComment(newRemediationComment)}
                onRemediationDateChange={(newRemediationDate) => setRemediationDate(newRemediationDate)}
                onVendorStatusChange={handleVendorStatusChange}
                handleWillNotRemediateReasonChange={(reason) => setWillNotRemediateReason(reason)}
                onVFilesChange={handleSetNVFile}
              />
              <StandardDialogActions>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
                  <Button variant='outlined' onClick={() => handleRemediationModalClose()}>
                    {user && user.type === USER_TYPES.VENDOR_ADMIN ? 'Cancel' : 'Close'}
                  </Button>
                  {user && user.type === USER_TYPES.VENDOR_ADMIN && !lockRemediation && (
                    <Button
                    variant='contained'
                    color='primary'
                    disabled={showSavingCircle}
                    startIcon={showSavingCircle ? <CircularProgress size={18} /> : null}
                    onClick={() => handleUpdateRemediation()}>
                      Update Remediation
                    </Button>
                  )}
                </Box>
              </StandardDialogActions>
            </StandardDialog>
            {company && (
              <StandardDialog
                title='Certification Upload'
                handleClose={handleUploadDocumentClose}
                isOpen={uploadDocumentOpen}
              >
                <CompanyCertificationForm
                  company={company}
                  handleClose={() => {
                    handleUploadDocumentClose();
                  }}
                  uploadCallback={companyCallback}
                  site={survey ? survey.site : undefined}
                  application={survey ? survey.application : undefined}
                />
              </StandardDialog>
            )}
            <StandardDialog
              title={`${
                companyCerts && companyCerts.length > 0 ? companyCerts[companyCerts.length - 1].certification.title : ''
              } Certification Uploaded`}
              handleClose={handleCertUploadedClose}
              isOpen={certUploadedOpen}
            >
              <Typography>
                You have successfully Self Attested. All questions satisfied by this{' '}
                {companyCerts && companyCerts.length > 0
                  ? companyCerts[companyCerts.length - 1].certification.title
                  : ''}{' '}
                will be automatically answered in the TPN MPA Best Practices Questionnaire.
              </Typography>
              <StandardDialogActions>
                <Button variant='outlined' onClick={() => handleCertUploadedClose()}>
                  Close
                </Button>
                <Link to='/profile/'>
                  <Button variant='contained' color='primary'>
                    Profile
                  </Button>
                </Link>
                <Link to={`/questionnaire/${newSurveyId}/`}>
                  <Button variant='contained' color='primary'>
                    TPN MPA Best Practices Questionnaire
                  </Button>
                </Link>
              </StandardDialogActions>
            </StandardDialog>
            <StandardDialog
                title={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                    <img
                      src={ verificationAwardModalType === GOLD_NEXT_GEN_BADGE_LABEL ? GOLD_NEXT_GEN_BADGE_IMAGE : GOLD_STAR_BADGE_IMAGE }
                      alt="Badge"
                      style={{ width: 37, height: 37, marginRight: 5, verticalAlign: 'middle' }}
                    />
                    {badgeTitle}
                  </span>
                }
              handleClose={() => setVerificationAwardModalType(undefined)}
              isOpen={!!verificationAwardModalType}
            >
              <Typography>
              You&#39;ve validated all Remediations for {siteOrApplicationName}.
              The {verificationAwardModalType === GOLD_NEXT_GEN_BADGE_LABEL ? 'Gold(NextGen)' : 'Gold Star'} Badge has been awarded to {company?.name} for the {questionnaire?.title} Questionnaire.
              </Typography>
              <StandardDialogActions>
                <Button variant='outlined' onClick={() => setVerificationAwardModalType(undefined)}>
                  Close
                </Button>
              </StandardDialogActions>
            </StandardDialog>
          </>
      )}
    </>
  );
};

export default QuestionOnQuestionnaire;
